import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const fr: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Répertoire des styles de citation introuvable",
    metaTitle: "Styles de citation commençant par {label}",
    metaDescription:
      "Parcourez {count} styles Citation Style Language commençant par {label}. Ouvrez un style pour voir de vrais exemples de citations et un générateur gratuit.",
    ogSubtitle: "{count} styles avec exemples et générateurs dédiés.",
    ogKind: "Répertoire A–Z",
    schemaName: "Styles de citation commençant par {label}",
    schemaDescription:
      "Répertoire alphabétique de {count} styles Citation Style Language commençant par {label}.",
    breadcrumbStyles: "Styles de citation",
    heading: "Styles de citation commençant par {label}",
    intro:
      "Parcourez les {count} styles de cette partie du catalogue CSL. Chaque page contient les exemples formatés du style et un générateur de citations gratuit.",
    navLabel: "Styles de citation de A à Z",
    listHeading: "Tous les styles commençant par {label}",
  },
  nav: {
    tools: "Outils",
    guides: "Guides",
    styles: "Tous les styles",
    bibliography: "Ma bibliographie",
    search: "Rechercher",
    glossary: "Glossaire",
    home: "Accueil",
    menu: "Menu",
  },

  home: {
    title: "Générateur de citations gratuit",
    subtitle: "Mis à jour pour 2026",
    intro:
      "Collez un DOI, une URL ou une entrée BibTeX — ou cherchez un livre — et obtenez une référence correctement formatée en APA, MLA, Chicago, Vancouver, Harvard et des milliers d'autres styles. Entièrement gratuit, sans abonnement payant et sans inscription.",
    browseAllStyles: "Parcourir les {count} styles de citation →",
    featuresHeading: "Tout ce qu'il faut pour citer",
    featureStyles: "{count} styles",
    featureStylesDetail: "APA, MLA, Chicago et plus",
    featureBibliographies: "Plusieurs bibliographies",
    featureBibliographiesDetail: "Organisez par projet",
    featureBookSearch: "Recherche de livres",
    featureBookSearchDetail: "Par titre ou par auteur",
    featureExport: "Exportez partout",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Mode sombre",
    featureDarkModeDetail: "Reposant pour les yeux",
    featureNoSignup: "Sans inscription",
    featureNoSignupDetail: "Gratuit à vie, sans abonnement payant",
    aboutHeading:
      "Un générateur de citations gratuit pour étudiants et chercheurs",
    aboutBody:
      "CitationEasy prend en charge plus de 10 000 styles de citation académiques. Que vous ayez besoin d'APA en psychologie, de MLA en lettres, de Chicago en histoire ou de Vancouver pour les revues médicales, la mise en forme provient de la définition officielle du style lui-même, et non d'une approximation.",
    howItWorksHeading: "Comment ça marche",
    howItWorksStep1:
      "Collez un DOI, une URL, un ISBN, un identifiant PubMed ou arXiv, ou une entrée BibTeX ; vous pouvez aussi chercher un livre par son titre.",
    howItWorksStep2:
      "Choisissez votre style de citation. Tapez pour chercher parmi les {count} disponibles.",
    howItWorksStep3:
      "Copiez la référence obtenue, ou enregistrez-la dans une bibliographie et exportez-la vers Word, BibTeX ou RIS.",
    privacyNote:
      "Votre bibliographie est stockée dans votre propre navigateur. Aucun compte, et rien n'est envoyé sur un serveur.",
  },

  hubs: {
    toolsTitle: "Outils gratuits de rédaction et de citation",
    toolsIntro:
      "{count} outils qui fonctionnent dans le navigateur pour rédiger et citer : générateurs pour chaque type de source, compteurs de mots et de caractères, analyse de lisibilité, outils de mise en forme et convertisseurs. Tous gratuits et instantanés.",
    stylesTitle: "Parcourir tous les styles de citation",
    stylesIntro:
      "Les {count} styles du projet Citation Style Language, avec recherche et filtres par pays, par sujet et par discipline.",
    guidesTitle: "Guides de citation et d'écriture académique",
    guidesIntro:
      "Des guides détaillés sur la façon de citer chaque type de source, d'évaluer ce que vous trouvez et de mieux écrire.",
    howToCiteTitle: "Comment citer n'importe quelle source",
    howToCiteIntro:
      "Des pages pas à pas qui associent chaque type de source courant aux principaux styles de citation, chacune avec un exemple concret.",
    faqTitle: "Questions fréquentes sur la citation",
    faqIntro:
      "Des réponses courtes et exactes aux questions que les étudiants se posent vraiment au moment de citer.",
    glossaryTitle: "Glossaire de la citation",
    glossaryIntro:
      "Des définitions claires des termes qui reviennent dans les manuels de style et les consignes de devoir.",
    openHub: "Ouvrir",
  },

  styleLanding: {
    metaTitle: "Générateur de citations {style} gratuit",
    metaDescription:
      "Générez gratuitement des citations {style}. Collez un DOI, une URL, un ISBN ou du BibTeX et obtenez la référence et l'appel de citation au format {style}.",
    heading: "Générateur de citations {style}",
    intro:
      "Créez des citations {style} exactes pour des livres, des articles de revue et des pages web en un clic. Gratuit et sans inscription.",
    aboutHeading: "À propos du style {style}",
    whenToUseHeading: "Quand utiliser {style}",
    factsHeading: "{style} en bref",
    factEdition: "Édition",
    factSystem: "Système de citation",
    factInText: "Format dans le texte",
    factReferenceList: "Nom de la liste",
    factFields: "Courant en",
    systemAuthorDate: "Auteur–date",
    systemNumbered: "Numérique",
    systemFootnotes: "Notes de bas de page",
    howToHeading: "Comment utiliser ce générateur {style}",
    howToStep1:
      "Collez une URL, un DOI, un ISBN ou une entrée BibTeX dans le champ ci-dessus, ou cherchez un livre par son titre.",
    howToStep2:
      "{style} est déjà sélectionné sur cette page : vous pouvez générer directement.",
    howToStep3:
      "Copiez le résultat dans votre document, ou enregistrez-le et exportez la liste complète plus tard.",
    relatedHeading: "Plus de ressources sur {style}",
    englishOnlyNote:
      "Les guides détaillés sur {style} et les exemples par type de source sont disponibles en anglais.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Style de citation introuvable",
    metaTitle: "Générateur de citations {style} - format {style} gratuit",
    metaFormatAndFields: "{style} est un style {format} utilisé en {fields}.",
    metaFormatOnly: "{style} est un style {format}.",
    metaFieldsOnly: "{style} est utilisé en {fields}.",
    metaPromiseNote:
      "Consultez une vraie note de bas de page en {style} et générez la vôtre gratuitement.",
    metaPromiseExamples:
      "Consultez de vrais exemples de citations {style} pour un article de revue, un livre et un site web, et générez les vôtres gratuitement.",
    ogKindFormat: "Style {format}",
    ogKindDefault: "Style de citation",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "et",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Style de citation pour {name}. Du dépôt Citation Style Language.",
    genericWhenUsed:
      "Lors d'un envoi à des publications utilisant le style {name}, ou d'une citation dans ces publications.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "anthropologie",
    disciplineAstronomy: "astronomie",
    disciplineBiology: "biologie",
    disciplineBotany: "botanique",
    disciplineChemistry: "chimie",
    disciplineCommunications: "communication",
    disciplineEngineering: "ingénierie",
    disciplineGenericBase: "général",
    disciplineGeography: "géographie",
    disciplineGeology: "géologie",
    disciplineHistory: "histoire",
    disciplineHumanities: "sciences humaines",
    disciplineLaw: "droit",
    disciplineLinguistics: "linguistique",
    disciplineLiterature: "littérature",
    disciplineMath: "mathématiques",
    disciplineMedicine: "médecine",
    disciplinePhilosophy: "philosophie",
    disciplinePhysics: "physique",
    disciplinePoliticalScience: "sciences politiques",
    disciplinePsychology: "psychologie",
    disciplineScience: "sciences",
    disciplineSocialScience: "sciences sociales",
    disciplineSociology: "sociologie",
    disciplineTheology: "théologie",
    disciplineZoology: "zoologie",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "un style auteur-date : les citations dans le texte indiquent l'auteur et l'année, et la liste de références est classée par ordre alphabétique d'auteur",
    formatClassNumeric:
      "un style numérique : les sources sont numérotées dans l'ordre de leur première citation, et les citations dans le texte reprennent ces numéros",
    formatClassNote:
      "un style à notes : les citations apparaissent en notes de bas de page ou en notes de fin, plutôt qu'entre parenthèses dans le texte",
    formatClassLabel:
      "un style à étiquette : les citations dans le texte utilisent une étiquette compacte construite à partir du nom de l'auteur et de l'année",
    formatClassAuthor:
      "un style auteur seul : les citations dans le texte indiquent l'auteur, les détails complets figurant dans la liste de références",
    formatClassOther: "un style {format}",
    breadcrumbStyles: "Styles de citation",
    internationalLabel: "International",
    heading: "Générateur de citations {style}",
    intro:
      "Créez des citations {style} exactes pour des livres, des articles et des sites web en un clic.",
    examplesHeading: "Exemples de citations {style}",
    examplesIntro: "Les trois mêmes sources mises en forme en {style} :",
    examplesIntroWithFormat:
      "Les trois mêmes sources mises en forme en {style} — {format} :",
    examplesNoteOnlyIntro:
      "{style} est un style à notes : il n'a pas de liste de références, et chaque citation est une note de bas de page. Voici notre exemple d'article de revue cité en {style} :",
    labelJournalArticle: "Article de revue",
    labelBook: "Livre",
    labelWebsite: "Site web",
    labelFootnote: "Note de bas de page (article de revue)",
    labelInText: "Citation dans le texte (pour l'article de revue)",
    aboutHeading: "À propos du style {style}",
    whenToUseLabel: "Quand utiliser {style} :",
    commonInLabel: "Courant en :",
    subjectAreasLabel: "Domaines d'étude :",
    formattingRulesLabel: "Règles de mise en forme :",
    parentStylePrefix: "{style} suit la mise en forme de",
    parentStyleSuffix: ", le style parent dont il est dérivé.",
    howToHeading: "Comment utiliser ce générateur {style}",
    howToBody:
      "Collez un DOI, l'URL d'un article de presse ou d'une page web, ou du BibTeX brut dans le champ ci-dessus. Vous pouvez aussi rechercher un livre par titre ou par auteur et le citer par son ISBN. Choisissez «{style}» dans le menu déroulant des styles (il est déjà présélectionné sur cette page), puis cliquez sur «Générer la référence». Copiez le résultat dans votre liste de références ou votre bibliographie. Vous pouvez modifier l'entrée générée (par exemple corriger un titre ou ajouter une date) avant de la copier.",
    faqHeading: "Questions fréquentes sur {style}",
    faqWhatIsQ: "Qu'est-ce que {style} ?",
    faqWhenQ: "Quand utiliser {style} ?",
    faqInTextQ: "À quoi ressemble une citation dans le texte en {style} ?",
    faqInTextA:
      "{style} est {format}. Citer notre exemple d'article de revue dans le texte ressemble à ceci : {example}",
    faqReferenceQ:
      "À quoi ressemble une entrée de la liste de références en {style} ?",
    faqReferenceA:
      "Un article de revue mis en forme en {style} ressemble à ceci : {example}",
    faqWhereQ: "Où {style} est-il couramment utilisé ?",
    faqWhereA: "{style} est utilisé {region}. {whenUsed}",
    faqRegionDefault: "à l'échelle internationale",
    faqGenerateQ: "Comment générer une citation en {style} ?",
    faqGenerateA:
      "Utilisez le générateur gratuit sur cette page : collez un DOI, une URL (par exemple un article de presse) ou du BibTeX, ou recherchez un livre par titre ou par auteur. Sélectionnez {style} comme style de citation et cliquez sur Générer pour obtenir une référence mise en forme que vous pourrez copier dans votre devoir ou votre bibliographie.",
    footerSupports:
      "prend en charge de nombreux styles de citation du monde entier. Notre générateur utilise des styles Citation Style Language (CSL) provenant du",
    footerRepoLink: "dépôt officiel CSL",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Générer cette citation",
    generateBody:
      "Collez une URL, un DOI, un ISBN ou les informations de la source et obtenez une citation {style} prête à l'emploi pour {source}.",
    generateCta: "Ouvrir le {tool} →",
    guideHeading: "Pour aller plus loin",
    guideCta: "Lire le guide complet : {guide}",
    howToCiteHeading: "Comment citer {source} dans chaque style",
    howToCiteBody:
      "Règles de mise en forme, modèles et exemples détaillés pour les styles les plus demandés.",
    howToCiteMore: "Tous les types de sources et de styles →",
    citeNowHeading: "Citer {source} en {style} maintenant",
    citeNowHowTo: "Règles de mise en forme et exemples détaillés",
    citeNowTool: "Générer avec le {tool}",
    citeNowHub: "Générateur de citations {style}",
    hubSourcesHeading: "Comment citer les sources courantes en {style}",
    hubGuidesHeading: "Guides {style}",
    hubCompareHeading: "Comparer {style} à d'autres styles",
    moreInCollection: "Autres outils : {collection}",
    seeAllInCollection: "Tout voir : {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Page introuvable",
    title: "Comment citer {source} en {style} ({edition})",
    metaTitle: "Comment citer {source} en {style} [{edition}, mis à jour 2026]",
    metaDescription:
      "Apprenez à citer {description} au format {style} ({edition}). Exemples gratuits, modèles et guides de citation dans le texte.",
    keywordCite: "citer {source} en {style}",
    keywordCitation: "citation {style} pour {source}",
    keywordHowTo: "comment citer {source}",
    keywordExample: "exemple de citation {style}",
    keywordReference: "référence {style} pour {source}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Liste des ouvrages cités",
    refListWorksCitedPage: "liste des ouvrages cités",
    refListBibliography: "Bibliographie",
    refListBibliographyLower: "bibliographie",
    refListReferenceList: "Liste de références",
    refListReferenceListLower: "liste de références",
    backLink: "← Tous les guides de citation",
    lead: "Apprenez à citer {description} au format {style} ({edition}). Ce guide comprend le format de la liste de références, des exemples de citation dans le texte et les principales règles de mise en forme.",
    referenceFormatHeading: "Format {list} en {style}",
    referenceFormatIntro:
      "Voici le format {style} standard pour citer {source} dans votre {list} :",
    noTemplateNote:
      "Suivez le modèle standard de {style} pour ce type de source.",
    workedHeading: "Exemple {style} pour {source}",
    workedIntro:
      "Une référence {style} complète pour {source}, mise en forme selon la définition officielle du style CSL {style} — le même moteur que celui du générateur de ce site :",
    workedNoteOnlyIntro:
      "{style} ne prévoit pas d'entrée pour {source} dans votre {list} — la source est citée uniquement en note. Voici cette note, mise en forme selon la définition officielle du style CSL {style} :",
    workedOscolaNote:
      "Les sorties OSCOLA générées sur ce site suivent le style CSL de la 4e édition, car aucun style CSL de la 5e édition n'est encore publié dans le dépôt Citation Style Language. Ajustez-les à la main : remplacez chaque ibid par le nom abrégé et (n X), utilisez les identifiants de juridiction de la 5e édition, mettez la forme abrégée d'une loi entre guillemets simples et préférez un lien permanent ou un DOI à la date de consultation",
    workedCheckedIntro:
      "Une référence {style} complète pour {source}, rédigée à la main et vérifiée d'après les consignes officielles de {style} pour ce type de source :",
    workedCheckedNoteOnlyIntro:
      "{style} ne prévoit pas d'entrée pour {source} dans votre {list} — la source est citée uniquement en note. Voici cette note, rédigée à la main et vérifiée d'après les consignes officielles de {style} :",
    workedInTextLabel: "Citation dans le texte correspondante :",
    inTextHeading: "Citation dans le texte en {style}",
    inTextIntro:
      "Utilisez l'un de ces formats pour citer cette source dans votre devoir :",
    inTextParenthetical: "Parenthétique",
    inTextNarrative: "Narrative",
    rulesHeading: "Principales règles de mise en forme en {style}",
    stepsHeading: "Guide étape par étape",
    step1Label: "Rassemblez les informations sur la source :",
    step1Body:
      "Pour {source}, réunissez le nom de l'auteur, le titre, la date de publication, ainsi que l'URL ou le DOI.",
    step2Label: "Mettez en forme la référence :",
    step2Body:
      "Organisez les éléments selon le modèle {style} ({edition}) présenté ci-dessus.",
    step3Label: "Créez la citation dans le texte :",
    step3Body:
      "Ajoutez une citation parenthétique ou narrative dans votre devoir à l'endroit où vous mentionnez cette source.",
    step4Label: "Ajoutez-la à votre {list} :",
    step4Body:
      "Intégrez la citation complète et mise en forme à la fin de votre devoir.",
    step5Label: "Vérifiez la mise en forme :",
    step5Body:
      "Vérifiez que l'italique, la ponctuation, les majuscules et les retraits négatifs respectent les exigences du style {style}.",
    ctaHeading: "Générez cette citation automatiquement",
    ctaBody:
      "Évitez la mise en forme manuelle. Collez un DOI, une URL ou un ISBN dans CitationEasy et obtenez une citation {style} parfaitement mise en forme en quelques secondes.",
    ctaButton: "Citer en {style} maintenant →",
    otherStylesHeading: "Citer {source} dans d'autres styles",
    comboLink: "{source} en {style}",
    otherSourcesHeading: "Citer d'autres sources en {style}",
    resourcesHeading: "Plus de ressources",
    resourceGenerator: "Générateur de citations gratuit ({count} styles)",
    resourceTools: "Outils d'écriture gratuits",
    resourceCompare: "Comparer les styles de citation",
    resourceGuides: "Tous les guides de citation",
    resourceCheatSheets: "Aide-mémoire (APA, MLA, Chicago)",
    resourceGlossary:
      "Glossaire de la citation (et al., ibid., DOI, retrait négatif)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Rassembler les informations sur la source",
    schemaStep1Text:
      "Réunissez toutes les informations nécessaires pour citer {source} : auteur, titre, date, ainsi que l'URL ou le DOI.",
    schemaStep2Name: "Mettre en forme en {style}",
    schemaStep2Text:
      "Suivez les règles de mise en forme {style} ({edition}) pour organiser les éléments de la citation dans le bon ordre.",
    schemaStep3Name: "Créer la citation dans le texte",
    schemaStep3Text:
      "Ajoutez la citation dans le texte correspondante dans votre devoir, au format {style}.",
    schemaStep4Name: "Ajouter à la liste de références",
    schemaStep4Text:
      "Ajoutez la citation complète dans votre {list}, à la fin de votre devoir.",
    faqHowQ: "Comment citer {source} en {style} ?",
    faqHowA:
      "Pour citer {source} en {style} ({edition}), indiquez l'auteur, le titre, la date et la localisation de la source. Utilisez notre générateur de citations gratuit pour une mise en forme automatique.",
    faqFormatQ: "Quel est le format {style} pour citer {source} ?",
    faqFormatA: "Le format {style} pour {source} est : {example}",
    faqFormatFallback:
      "Le format {style} exige des éléments précis — auteur, titre, date, localisation de la source — organisés dans un ordre particulier.",
    faqInTextQ:
      "À quoi ressemble la citation dans le texte pour {source} en {style} ?",
    faqInTextA:
      "En {style}, la citation dans le texte de l'exemple ci-dessus est {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // French: Name is the bare label ("Livre"); Title, Phrase and Noun carry the article ("un livre", "la Bible").
  howToCiteSources: {
    websiteName: "Site web",
    websiteTitle: "un site web",
    websitePhrase: "un site web",
    websiteNoun: "un site web",
    websiteDescription:
      "des pages web, des articles en ligne et du contenu web",

    bookName: "Livre",
    bookTitle: "un livre",
    bookPhrase: "un livre",
    bookNoun: "un livre",
    bookDescription: "des livres imprimés et numériques",

    journalArticleName: "Article de revue",
    journalArticleTitle: "un article de revue",
    journalArticlePhrase: "un article de revue",
    journalArticleNoun: "un article de revue",
    journalArticleDescription:
      "des articles de revues universitaires et à comité de lecture",

    youtubeVideoName: "Vidéo YouTube",
    youtubeVideoTitle: "une vidéo YouTube",
    youtubeVideoPhrase: "une vidéo YouTube",
    youtubeVideoNoun: "une vidéo YouTube",
    youtubeVideoDescription: "des vidéos YouTube et d'autres vidéos en ligne",

    podcastName: "Podcast",
    podcastTitle: "un podcast",
    podcastPhrase: "un podcast",
    podcastNoun: "un podcast",
    podcastDescription: "des épisodes et des séries de podcast",

    newspaperArticleName: "Article de journal",
    newspaperArticleTitle: "un article de journal",
    newspaperArticlePhrase: "un article de journal",
    newspaperArticleNoun: "un article de journal",
    newspaperArticleDescription:
      "des articles de journaux imprimés et en ligne",

    magazineArticleName: "Article de magazine",
    magazineArticleTitle: "un article de magazine",
    magazineArticlePhrase: "un article de magazine",
    magazineArticleNoun: "un article de magazine",
    magazineArticleDescription:
      "des articles de magazines imprimés et en ligne",

    blogPostName: "Article de blog",
    blogPostTitle: "un article de blog",
    blogPostPhrase: "un article de blog",
    blogPostNoun: "un article de blog",
    blogPostDescription: "des articles de blog et des tribunes en ligne",

    wikipediaName: "Wikipédia",
    wikipediaTitle: "Wikipédia",
    wikipediaPhrase: "Wikipédia",
    wikipediaNoun: "Wikipédia",
    wikipediaDescription: "des articles de Wikipédia et d'autres wikis",

    pdfName: "PDF",
    pdfTitle: "un PDF",
    pdfPhrase: "un PDF",
    pdfNoun: "un PDF",
    pdfDescription: "des documents et des rapports au format PDF",

    interviewName: "Entretien",
    interviewTitle: "un entretien",
    interviewPhrase: "un entretien",
    interviewNoun: "un entretien",
    interviewDescription: "des entretiens personnels, publiés ou diffusés",

    socialMediaName: "Publication sur les réseaux sociaux",
    socialMediaTitle: "une publication sur les réseaux sociaux",
    socialMediaPhrase: "une publication sur les réseaux sociaux",
    socialMediaNoun: "une publication sur les réseaux sociaux",
    socialMediaDescription:
      "des tweets, des publications Instagram ou Facebook et des vidéos TikTok",

    governmentDocumentName: "Document gouvernemental",
    governmentDocumentTitle: "un document gouvernemental",
    governmentDocumentPhrase: "un document gouvernemental",
    governmentDocumentNoun: "un document gouvernemental",
    governmentDocumentDescription:
      "des rapports gouvernementaux, des textes législatifs et des documents officiels",

    dissertationName: "Thèse ou mémoire",
    dissertationTitle: "une thèse ou un mémoire",
    dissertationPhrase: "une thèse ou un mémoire",
    dissertationNoun: "une thèse ou un mémoire",
    dissertationDescription: "des thèses de doctorat et des mémoires de master",

    conferencePaperName: "Communication de conférence",
    conferencePaperTitle: "une communication de conférence",
    conferencePaperPhrase: "une communication de conférence",
    conferencePaperNoun: "une communication de conférence",
    conferencePaperDescription: "des actes de conférence et des présentations",

    lectureNotesName: "Cours magistral ou PowerPoint",
    lectureNotesTitle: "un cours magistral ou un PowerPoint",
    lectureNotesPhrase: "un cours magistral ou un PowerPoint",
    lectureNotesNoun: "un cours magistral ou un PowerPoint",
    lectureNotesDescription:
      "des cours magistraux, des diapositives et des supports de cours",

    webinarName: "Webinaire",
    webinarTitle: "un webinaire",
    webinarPhrase: "un webinaire",
    webinarNoun: "un webinaire",
    webinarDescription:
      "des webinaires et des séminaires en ligne, en direct ou enregistrés",

    onlineCourseName: "Cours en ligne",
    onlineCourseTitle: "un cours en ligne",
    onlineCoursePhrase: "un cours en ligne",
    onlineCourseNoun: "un cours en ligne",
    onlineCourseDescription:
      "des cours en ligne et des MOOC (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "Présentation PowerPoint",
    presentationSlidesTitle: "une présentation PowerPoint",
    presentationSlidesPhrase: "une présentation PowerPoint",
    presentationSlidesNoun: "une présentation PowerPoint",
    presentationSlidesDescription:
      "des présentations PowerPoint et d'autres diaporamas",

    imageName: "Image ou photographie",
    imageTitle: "une image ou une photographie",
    imagePhrase: "une image ou une photographie",
    imageNoun: "une image ou une photographie",
    imageDescription:
      "des photographies, des œuvres d'art, des graphiques et des images en ligne",

    movieName: "Film",
    movieTitle: "un film",
    moviePhrase: "un film",
    movieNoun: "un film",
    movieDescription: "un film",

    documentaryName: "Documentaire",
    documentaryTitle: "un documentaire",
    documentaryPhrase: "un documentaire",
    documentaryNoun: "un documentaire",
    documentaryDescription:
      "des films documentaires et d'autres films de non-fiction",

    tvShowName: "Émission de télévision",
    tvShowTitle: "une émission de télévision",
    tvShowPhrase: "une émission de télévision",
    tvShowNoun: "une émission de télévision",
    tvShowDescription: "une émission ou une série télévisée",

    songName: "Chanson ou morceau de musique",
    songTitle: "une chanson ou un morceau de musique",
    songPhrase: "une chanson ou un morceau de musique",
    songNoun: "une chanson ou un morceau de musique",
    songDescription: "une chanson ou un enregistrement musical",

    poemName: "Poème",
    poemTitle: "un poème",
    poemPhrase: "un poème",
    poemNoun: "un poème",
    poemDescription:
      "des poèmes et des vers tirés d'anthologies ou de recueils",

    courtCaseName: "Décision de justice",
    courtCaseTitle: "une décision de justice",
    courtCasePhrase: "une décision de justice",
    courtCaseNoun: "une décision de justice",
    courtCaseDescription: "une affaire judiciaire ou une décision de justice",

    patentName: "Brevet",
    patentTitle: "un brevet",
    patentPhrase: "un brevet",
    patentNoun: "un brevet",
    patentDescription:
      "un brevet ou tout autre dépôt de propriété intellectuelle",

    encyclopediaName: "Article d'encyclopédie",
    encyclopediaTitle: "un article d'encyclopédie",
    encyclopediaPhrase: "un article d'encyclopédie",
    encyclopediaNoun: "un article d'encyclopédie",
    encyclopediaDescription: "un article ou une entrée d'encyclopédie",

    dictionaryName: "Entrée de dictionnaire",
    dictionaryTitle: "une entrée de dictionnaire",
    dictionaryPhrase: "une entrée de dictionnaire",
    dictionaryNoun: "une entrée de dictionnaire",
    dictionaryDescription: "une définition ou une entrée de dictionnaire",

    datasetName: "Jeu de données",
    datasetTitle: "un jeu de données",
    datasetPhrase: "un jeu de données",
    datasetNoun: "un jeu de données",
    datasetDescription: "un jeu de données ou une base de données",

    softwareName: "Logiciel ou application",
    softwareTitle: "un logiciel ou une application",
    softwarePhrase: "un logiciel ou une application",
    softwareNoun: "un logiciel ou une application",
    softwareDescription:
      "un logiciel, une application ou un programme informatique",

    videoGameName: "Jeu vidéo",
    videoGameTitle: "un jeu vidéo",
    videoGamePhrase: "un jeu vidéo",
    videoGameNoun: "un jeu vidéo",
    videoGameDescription: "des jeux vidéo et des logiciels de jeu interactifs",

    pressReleaseName: "Communiqué de presse",
    pressReleaseTitle: "un communiqué de presse",
    pressReleasePhrase: "un communiqué de presse",
    pressReleaseNoun: "un communiqué de presse",
    pressReleaseDescription:
      "un communiqué de presse ou un communiqué officiel",

    reportName: "Rapport",
    reportTitle: "un rapport",
    reportPhrase: "un rapport",
    reportNoun: "un rapport",
    reportDescription: "un rapport ou un livre blanc",

    preprintName: "Prépublication",
    preprintTitle: "une prépublication",
    preprintPhrase: "une prépublication",
    preprintNoun: "une prépublication",
    preprintDescription:
      "des articles déposés sur arXiv, bioRxiv et d'autres serveurs de prépublications",

    bookChapterName: "Chapitre de livre",
    bookChapterTitle: "un chapitre de livre",
    bookChapterPhrase: "un chapitre de livre",
    bookChapterNoun: "un chapitre de livre",
    bookChapterDescription:
      "des chapitres et des contributions dans des ouvrages collectifs",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT",
    chatgptDescription: "ChatGPT et d'autres outils d'IA générative",

    bibleName: "Bible",
    bibleTitle: "la Bible",
    biblePhrase: "la Bible",
    bibleNoun: "la Bible",
    bibleDescription: "la Bible, les Écritures et d'autres textes sacrés",

    tedTalkName: "Conférence TED",
    tedTalkTitle: "une conférence TED",
    tedTalkPhrase: "une conférence TED",
    tedTalkNoun: "une conférence TED",
    tedTalkDescription:
      "des conférences TED et d'autres conférences enregistrées",

    emailName: "E-mail",
    emailTitle: "un e-mail",
    emailPhrase: "un e-mail",
    emailNoun: "un e-mail",
    emailDescription:
      "des e-mails, des lettres et des communications personnelles",

    redditName: "Publication Reddit ou message de forum",
    redditTitle: "une publication Reddit ou un message de forum",
    redditPhrase: "une publication Reddit",
    redditNoun: "une publication Reddit",
    redditDescription:
      "des fils Reddit, des AMA et des messages publiés sur des forums ou des groupes de discussion en ligne",

    tweetName: "Publication sur X (Twitter)",
    tweetTitle: "une publication sur X (Twitter)",
    tweetPhrase: "une publication sur X",
    tweetNoun: "une publication sur X",
    tweetDescription:
      "des tweets et des publications sur X (anciennement Twitter)",

    mapName: "Carte",
    mapTitle: "une carte",
    mapPhrase: "une carte",
    mapNoun: "une carte",
    mapDescription:
      "des cartes imprimées, des atlas et des cartes interactives comme Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude",
    claudeDescription:
      "des conversations avec Claude et d'autres contenus générés par l'IA d'Anthropic",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini",
    geminiDescription:
      "des conversations avec Google Gemini et des contenus générés par l'IA",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot",
    copilotDescription:
      "des conversations et des réponses d'IA de Microsoft Copilot",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity",
    perplexityDescription:
      "des réponses et des fils de discussion de Perplexity, ainsi que les sources qu'ils citent",

    aiImageName: "Image générée par l'IA",
    aiImageTitle: "une image générée par l'IA",
    aiImagePhrase: "une image générée par l'IA",
    aiImageNoun: "une image générée par l'IA",
    aiImageDescription:
      "des images créées avec DALL·E, Midjourney, Gemini et d'autres outils d'IA",

    tiktokName: "Vidéo TikTok",
    tiktokTitle: "une vidéo TikTok",
    tiktokPhrase: "une vidéo TikTok",
    tiktokNoun: "une vidéo TikTok",
    tiktokDescription: "des vidéos TikTok et des publications de créateurs",

    instagramName: "Publication Instagram",
    instagramTitle: "une publication Instagram",
    instagramPhrase: "une publication Instagram",
    instagramNoun: "une publication Instagram",
    instagramDescription:
      "des publications, des reels et des stories Instagram",

    statuteName: "Loi ou texte législatif",
    statuteTitle: "une loi ou un texte législatif",
    statutePhrase: "une loi ou un texte législatif",
    statuteNoun: "une loi ou un texte législatif",
    statuteDescription:
      "des lois votées par le Parlement, des textes législatifs et des codes",

    isoStandardName: "Norme ISO",
    isoStandardTitle: "une norme ISO",
    isoStandardPhrase: "une norme ISO",
    isoStandardNoun: "une norme ISO",
    isoStandardDescription:
      "des normes ISO, IEC et d'autres normes techniques publiées",

    quranName: "Coran",
    quranTitle: "le Coran",
    quranPhrase: "le Coran",
    quranNoun: "le Coran",
    quranDescription: "le Coran, en arabe ou en traduction",

    ebookNoPageNumbersName: "Livre numérique sans numéros de page",
    ebookNoPageNumbersTitle: "un livre numérique sans numéros de page",
    ebookNoPageNumbersPhrase: "un livre numérique sans numéros de page",
    ebookNoPageNumbersNoun: "un livre numérique sans numéros de page",
    ebookNoPageNumbersDescription:
      "des livres numériques Kindle, EPUB et en ligne sans pagination fixe",

    speechName: "Discours",
    speechTitle: "un discours",
    speechPhrase: "un discours",
    speechNoun: "un discours",
    speechDescription:
      "des discours, des allocutions et des sermons, en direct ou enregistrés",

    letterName: "Lettre",
    letterTitle: "une lettre",
    letterPhrase: "une lettre",
    letterNoun: "une lettre",
    letterDescription:
      "des lettres publiées, des lettres d'archives et des lettres ouvertes",

    secondarySourceName: "Source secondaire",
    secondarySourceTitle: "une source secondaire",
    secondarySourcePhrase: "une source secondaire",
    secondarySourceNoun: "une source secondaire",
    secondarySourceDescription:
      "une source que vous avez trouvée citée dans un autre ouvrage",
  },

  // ---------------------------------------------------------------------------
  // NOT YET TRANSLATED. These five sections were copied verbatim from `en.ts` so
  // this file still satisfies `Dictionary`; the page types they cover
  // (`taxonomy`, `compare`, `convert`) are deliberately absent from
  // TRANSLATED_PAGE_TYPES, so nothing publishes them in this language yet. The
  // translation pass replaces the strings and only then adds the types.
  // ---------------------------------------------------------------------------
  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Introuvable",
    fieldMetaTitle:
      "Styles de citation en {field} - Meilleurs styles de référencement [2026]",
    fieldMetaDescription:
      "Découvrez les styles de citation les plus utilisés en {field}. Trouvez le format de référencement adapté à votre mémoire, votre thèse ou votre article de revue en {fieldLower}.",
    fieldOgTitle: "Styles de citation en {field}",
    fieldOgSubtitle: "Généralement exigés : {styles}.",
    fieldOgKind: "Par discipline académique",
    fieldKeywordStyle: "style de citation en {field}",
    fieldKeywordReferencing: "référencement en {field}",
    fieldKeywordBest: "meilleur style de citation en {fieldLower}",
    fieldKeywordBibliography: "format de bibliographie en {fieldLower}",
    fieldKeywordDiscipline: "styles de citation par discipline",
    fieldBackLink: "← Tous les styles de citation",
    fieldHeading: "Styles de citation en {field}",
    fieldRecommendedHeading: "Styles de citation recommandés",
    fieldRecommendedIntro:
      "Les styles de citation suivants sont couramment utilisés dans la recherche, les revues et les travaux universitaires en {field}. Sélectionnez un style pour en savoir plus et commencer à générer des citations.",
    fieldStyleFallback:
      "Un style de citation utilisé en {field} et dans les disciplines connexes.",
    fieldRelatedHeading: "Domaines connexes",
    fieldRelatedIntro:
      "Découvrez les styles de citation utilisés dans les disciplines académiques connexes :",
    fieldCtaHeading: "Découvrir tous les styles de citation",
    fieldCtaBody:
      "CitationEasy prend en charge {count} styles de citation dans toutes les disciplines académiques. Parcourez la collection complète pour trouver exactement le format dont vous avez besoin.",
    fieldCtaButton: "Parcourir tous les styles →",
    fieldFaqWhichQ: "Quel style de citation est utilisé en {field} ?",
    fieldFaqWhichA:
      "Le style de citation utilisé en {field} dépend de la revue, de l'établissement ou des consignes spécifiques de l'enseignant. Les styles courants incluent {styles}. Vérifiez toujours les consignes de votre devoir ou les exigences de la revue visée pour connaître le format requis.",
    fieldFaqSameQ:
      "Toutes les revues en {field} utilisent-elles le même style ?",
    fieldFaqSameA:
      "Non, les revues en {field} n'utilisent pas toutes le même style de citation. Certains styles sont plus courants dans ce domaine, mais chaque revue peut avoir ses propres exigences de mise en forme. Vérifiez toujours les consignes de soumission de la revue avant de préparer votre manuscrit.",
    fieldFaqChooseQ:
      "Comment choisir un style de citation pour mon mémoire en {field} ?",
    fieldFaqChooseA:
      "Pour choisir un style de citation pour votre mémoire en {field}, vérifiez d'abord si votre enseignant ou la revue visée impose un style particulier. Sinon, utilisez l'un des styles couramment recommandés en {field} listés ci-dessus. CitationEasy prend en charge plus de {count} styles et peut générer des citations dans n'importe lequel d'entre eux, gratuitement.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "Pays introuvable",
    countryMetaTitle: "Styles de citation pour {country} - APA, MLA et plus",
    countryMetaDescription:
      "Parcourez les styles de citation utilisés pour {country}. Générateur de citations gratuit pour {country}.",
    countryOgTitle: "Styles de citation utilisés pour {country}",
    countryOgKind: "Par pays",
    countryHeading: "{country} – Styles de citation",
    countryIntroOne:
      "{count} style de citation couramment utilisé pour {country} ou associé à ce pays. Sélectionnez-en un pour ouvrir un générateur dédié.",
    countryIntroMany:
      "{count} styles de citation couramment utilisés pour {country} ou associés à ce pays. Sélectionnez-en un pour ouvrir un générateur dédié.",
    countryBrowserTitle: "{country} – Styles de citation",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Type introuvable",
    typeMetaTitle: "Styles de citation en {type} - Générateur gratuit",
    typeMetaDescription:
      "Styles de citation en {typeLower}. APA, MLA, Vancouver et plus. Générateur de citations gratuit.",
    typeOgTitle: "Styles de citation en {type}",
    typeOgKind: "Par sujet",
    typeHeading: "Styles de citation en {type}",
    typeIntroOne:
      "{count} style de citation en {typeLower}. Sélectionnez-en un pour ouvrir un générateur dédié avec FAQ.",
    typeIntroMany:
      "{count} styles de citation en {typeLower}. Sélectionnez-en un pour ouvrir un générateur dédié avec FAQ.",
    typeBrowserTitle: "Styles de citation en {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Styles de citation",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Styles de citation",
    breadcrumbByCountry: "Par pays",
    breadcrumbByDiscipline: "Par discipline",
    allStylesLink: "← Tous les styles de citation",
    internationalLabel: "International",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psychologie",
    education: "Sciences de l'éducation",
    sociology: "Sociologie",
    anthropology: "Anthropologie",
    politicalScience: "Sciences politiques",
    economics: "Économie",
    business: "Commerce et gestion",
    literature: "Littérature et langues",
    history: "Histoire",
    philosophy: "Philosophie",
    law: "Droit et études juridiques",
    medicine: "Médecine et sciences cliniques",
    nursing: "Soins infirmiers et obstétrique",
    publicHealth: "Santé publique et épidémiologie",
    pharmacy: "Pharmacie",
    biology: "Biologie et sciences de la vie",
    chemistry: "Chimie",
    physics: "Physique",
    engineering: "Ingénierie",
    computerScience: "Informatique",
    mathematics: "Mathématiques et statistiques",
    environmentalScience: "Sciences de l'environnement",
    earthScience: "Sciences de la Terre et géologie",
    artHistory: "Histoire de l'art et arts visuels",
    music: "Musique et musicologie",
    theology: "Théologie et études religieuses",
    socialWork: "Travail social",
    linguistics: "Linguistique",
    architecture: "Architecture et urbanisme",
    filmMedia: "Études cinématographiques et médiatiques",
    journalism: "Journalisme et communication",
    criminology: "Criminologie et justice pénale",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Styles de citation couramment utilisés en psychologie, en sciences comportementales et en recherche cognitive.",
    education:
      "Styles de citation pour la recherche en éducation, la pédagogie et les études curriculaires.",
    sociology:
      "Styles de citation utilisés dans la recherche sociologique et la théorie sociale.",
    anthropology:
      "Styles de citation pour la recherche anthropologique et ethnographique.",
    politicalScience:
      "Styles de citation pour les sciences politiques, le gouvernement et la recherche en politiques publiques.",
    economics:
      "Styles de citation utilisés dans la recherche en économie et en économétrie.",
    business:
      "Styles de citation pour la recherche en commerce, gestion, marketing et finance.",
    literature:
      "Styles de citation pour les études littéraires, la linguistique et la recherche sur le langage.",
    history:
      "Styles de citation pour la recherche historique, l'historiographie et les études archivistiques.",
    philosophy:
      "Styles de citation pour l'écriture philosophique et la recherche en éthique.",
    law: "Styles de citation juridique pour les documents judiciaires, les revues de droit et les travaux académiques en droit.",
    medicine:
      "Styles de citation pour la recherche médicale, les études cliniques et la santé.",
    nursing:
      "Styles de citation pour la recherche en soins infirmiers et la pratique en santé.",
    publicHealth:
      "Styles de citation pour la recherche en santé publique et les études épidémiologiques.",
    pharmacy:
      "Styles de citation pour la recherche pharmaceutique et pharmacologique.",
    biology:
      "Styles de citation pour la recherche biologique, l'écologie et la génétique.",
    chemistry:
      "Styles de citation pour la chimie, la biochimie et la recherche en science des matériaux.",
    physics:
      "Styles de citation pour la physique, l'astrophysique et la recherche en physique appliquée.",
    engineering:
      "Styles de citation pour l'ingénierie et la recherche en technologie appliquée.",
    computerScience:
      "Styles de citation pour l'informatique, l'IA et la recherche en génie logiciel.",
    mathematics:
      "Styles de citation pour les mathématiques, les mathématiques appliquées et la recherche statistique.",
    environmentalScience:
      "Styles de citation pour la recherche environnementale et les études de durabilité.",
    earthScience:
      "Styles de citation pour la géologie, la géophysique et la recherche en sciences de la Terre.",
    artHistory:
      "Styles de citation pour l'histoire de l'art, la culture visuelle et les études muséales.",
    music:
      "Styles de citation pour la recherche musicale, la théorie et les études de performance.",
    theology:
      "Styles de citation pour les travaux académiques en théologie et en sciences religieuses.",
    socialWork:
      "Styles de citation pour la recherche et la pratique en travail social.",
    linguistics:
      "Styles de citation pour la linguistique, la phonétique et la science du langage.",
    architecture:
      "Styles de citation pour l'architecture, le design urbain et la recherche en aménagement.",
    filmMedia:
      "Styles de citation pour les études cinématographiques, télévisuelles, médiatiques et de communication.",
    journalism:
      "Styles de citation pour le journalisme, les études médiatiques et la recherche en communication.",
    criminology:
      "Styles de citation pour la criminologie et la recherche en justice pénale.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "États-Unis",
    unitedKingdom: "Royaume-Uni",
    australia: "Australie",
    canada: "Canada",
    newZealand: "Nouvelle-Zélande",
    ireland: "Irlande",
    southAfrica: "Afrique du Sud",
    germany: "Allemagne",
    france: "France",
    spain: "Espagne",
    italy: "Italie",
    netherlands: "Pays-Bas",
    belgium: "Belgique",
    switzerland: "Suisse",
    austria: "Autriche",
    portugal: "Portugal",
    luxembourg: "Luxembourg",
    sweden: "Suède",
    norway: "Norvège",
    denmark: "Danemark",
    finland: "Finlande",
    iceland: "Islande",
    poland: "Pologne",
    czechRepublic: "République tchèque",
    hungary: "Hongrie",
    romania: "Roumanie",
    russia: "Russie",
    ukraine: "Ukraine",
    greece: "Grèce",
    turkey: "Turquie",
    croatia: "Croatie",
    slovenia: "Slovénie",
    slovakia: "Slovaquie",
    serbia: "Serbie",
    bulgaria: "Bulgarie",
    lithuania: "Lituanie",
    latvia: "Lettonie",
    estonia: "Estonie",
    japan: "Japon",
    china: "Chine",
    southKorea: "Corée du Sud",
    india: "Inde",
    singapore: "Singapour",
    hongKong: "Hong Kong",
    taiwan: "Taïwan",
    malaysia: "Malaisie",
    indonesia: "Indonésie",
    thailand: "Thaïlande",
    vietnam: "Vietnam",
    philippines: "Philippines",
    pakistan: "Pakistan",
    bangladesh: "Bangladesh",
    sriLanka: "Sri Lanka",
    israel: "Israël",
    saudiArabia: "Arabie saoudite",
    unitedArabEmirates: "Émirats arabes unis",
    brazil: "Brésil",
    mexico: "Mexique",
    argentina: "Argentine",
    chile: "Chili",
    colombia: "Colombie",
    peru: "Pérou",
    venezuela: "Venezuela",
    egypt: "Égypte",
    nigeria: "Nigeria",
    kenya: "Kenya",
    morocco: "Maroc",
    international: "International",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Médecine",
    nursing: "Soins infirmiers",
    publicHealth: "Santé publique",
    pharmacy: "Pharmacie",
    dentistry: "Dentisterie",
    veterinary: "Sciences vétérinaires",
    psychology: "Psychologie",
    sociology: "Sociologie",
    anthropology: "Anthropologie",
    politicalScience: "Sciences politiques",
    economics: "Économie",
    education: "Sciences de l'éducation",
    socialWork: "Travail social",
    biology: "Biologie",
    chemistry: "Chimie",
    physics: "Physique",
    environmentalScience: "Sciences de l'environnement",
    earthScience: "Sciences de la Terre et géologie",
    agriculture: "Agriculture",
    ecology: "Écologie",
    engineering: "Ingénierie",
    computerScience: "Informatique",
    mathematics: "Mathématiques",
    statistics: "Statistiques",
    history: "Histoire",
    literature: "Littérature",
    philosophy: "Philosophie",
    linguistics: "Linguistique",
    artHistory: "Histoire de l'art",
    music: "Musique",
    filmMedia: "Cinéma et médias",
    archaeology: "Archéologie",
    law: "Droit",
    business: "Commerce",
    architecture: "Architecture",
    religion: "Religion et théologie",
    geography: "Géographie",
    general: "Général / Pluridisciplinaire",
    other: "Autre",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Liste de références",
    listWorksCited: "Liste des ouvrages cités",
    listReferences: "Références",
    listBibliography: "Bibliographie",
    listTableOfAuthorities: "Table des autorités",
    listReferencesCited: "Références citées",
    listLowerReferenceList: "liste de références",
    listLowerWorksCited: "liste des ouvrages cités",
    listLowerReferences: "références",
    listLowerBibliography: "bibliographie",
    listLowerTableOfAuthorities: "table des autorités",
    listLowerReferencesCited: "références citées",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Auteur, Année)",
    inTextAuthorPage: "(Auteur Page)",
    inTextAuthorYear: "(Auteur Année)",
    inTextSuperscriptOrBracketed: "Numéros en exposant ou entre crochets (1)",
    inTextBracketedNumbers: "Numéros entre crochets [1]",
    inTextSuperscriptNumbers: "Numéros en exposant¹",
    inTextSuperscriptOrAuthorYear: "Numéros en exposant ou (Auteur Année)",
    inTextKeyBased: "Références par clé",
    inTextSuperscriptOrAuthorCommaYear:
      "Numéros en exposant ou (Auteur, Année)",
    inTextFootnotesCaseLegislation:
      "Notes de bas de page avec citations de jurisprudence et de textes législatifs",
    inTextFootnotesSignals: "Notes de bas de page avec abréviations de signal",
    inTextFootnotesPinpoint: "Notes de bas de page avec renvois précis",
    inTextParenNumbers: "Numéros entre parenthèses (1)",
    inTextItalicNumbers: "Numéros en italique (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4e édition",
    edition5th: "5e édition",
    edition7th: "7e édition",
    edition8th: "8e édition",
    edition9th: "9e édition",
    edition11th: "11e édition",
    edition18th: "18e édition",
    edition22nd: "22e édition",
    editionCiteThemRight11th: "Cite Them Right, 11e édition",
    editionIcmjeStandard: "Norme ICMJE",
    editionStandard: "standard",
    editionCurrent: "actuelle",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "Acoustique",
    fieldAnthropology: "Anthropologie",
    fieldAppliedPhysics: "Physique appliquée",
    fieldArchaeology: "Archéologie",
    fieldArts: "Arts",
    fieldAstrophysics: "Astrophysique",
    fieldAustralianLaw: "Droit australien",
    fieldBiochemistry: "Biochimie",
    fieldBiology: "Biologie",
    fieldBiomedicalSciences: "Sciences biomédicales",
    fieldBiotechnology: "Biotechnologie",
    fieldBusiness: "Commerce",
    fieldCellBiology: "Biologie cellulaire",
    fieldChemicalEngineering: "Génie chimique",
    fieldChemistry: "Chimie",
    fieldClinicalResearch: "Recherche clinique",
    fieldComputerScience: "Informatique",
    fieldCondensedMatter: "Matière condensée",
    fieldCourtDocuments: "Documents judiciaires",
    fieldCriminology: "Criminologie",
    fieldCulturalStudies: "Études culturelles",
    fieldDemography: "Démographie",
    fieldEcology: "Écologie",
    fieldEducation: "Éducation",
    fieldElectronics: "Électronique",
    fieldEngineering: "Ingénierie",
    fieldEnvironmentalScience: "Sciences de l'environnement",
    fieldEpidemiology: "Épidémiologie",
    fieldEthnography: "Ethnographie",
    fieldGenetics: "Génétique",
    fieldGeology: "Géologie",
    fieldGlobalHealth: "Santé mondiale",
    fieldHealthSciences: "Sciences de la santé",
    fieldHistory: "Histoire",
    fieldHumanities: "Sciences humaines",
    fieldInformationSystems: "Systèmes d'information",
    fieldInformationTechnology: "Technologies de l'information",
    fieldInternationalRelations: "Relations internationales",
    fieldJurisprudence: "Jurisprudence",
    fieldLanguages: "Langues",
    fieldLaw: "Droit",
    fieldLawReview: "Revue juridique",
    fieldLegalStudies: "Études juridiques",
    fieldLifeSciences: "Sciences de la vie",
    fieldLiterature: "Littérature",
    fieldMaterialsScience: "Science des matériaux",
    fieldMathematics: "Mathématiques",
    fieldMedicine: "Médecine",
    fieldMolecularBiology: "Biologie moléculaire",
    fieldMultidisciplinary: "Pluridisciplinaire",
    fieldMultidisciplinaryScience: "Sciences pluridisciplinaires",
    fieldNaturalSciences: "Sciences naturelles",
    fieldNursing: "Soins infirmiers",
    fieldPharmacy: "Pharmacie",
    fieldPhysics: "Physique",
    fieldPoliticalScience: "Sciences politiques",
    fieldPsychology: "Psychologie",
    fieldPublicHealth: "Santé publique",
    fieldPublicPolicy: "Politiques publiques",
    fieldPublishing: "Édition",
    fieldScience: "Science",
    fieldSciences: "Sciences",
    fieldSocialSciences: "Sciences sociales",
    fieldSocialWork: "Travail social",
    fieldSociology: "Sociologie",
    fieldSoftwareEngineering: "Génie logiciel",
    fieldTheology: "Théologie",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "Le style de citation le plus utilisé en sciences sociales",
    mla: "Le style de citation standard pour les sciences humaines",
    chicago:
      "Le guide de style le plus fiable des États-Unis pour les auteurs, les éditeurs et les maisons d'édition",
    harvard:
      "Le style de référencement le plus utilisé dans les universités britanniques et australiennes",
    vancouver:
      "Le style de citation standard pour les revues médicales et biomédicales",
    ieee: "Le style de citation de référence en ingénierie et en informatique",
    ama: "Le standard de citation des revues médicales américaines",
    turabian: "La version simplifiée du style Chicago destinée aux étudiants",
    cse: "Le style de citation privilégié dans les sciences naturelles et physiques",
    bibtex:
      "Le format de bibliographie pour les utilisateurs de LaTeX et la composition académique",
    nature:
      "Le style de citation utilisé par la principale revue pluridisciplinaire au monde",
    acs: "Le standard de citation pour la chimie et les sciences chimiques",
    oscola:
      "Le style de citation juridique standard dans les facultés de droit et les revues britanniques",
    bluebook:
      "Le système de citation juridique de référence pour les tribunaux et les revues de droit américains",
    aglc: "Le style de citation juridique standard dans les facultés de droit et les tribunaux australiens",
    asa: "Le style de citation standard pour la sociologie et la recherche sociale",
    apsa: "Le style de citation pour la recherche et les revues de sciences politiques",
    aaa: "Le style de citation standard pour la recherche anthropologique",
    nlm: "Le standard de citation utilisé par PubMed et la Bibliothèque nationale de médecine des États-Unis",
    acm: "Le style de citation pour les conférences de l'ACM et la recherche en informatique",
    aps: "Le style de citation de Physical Review et des revues de l'APS",
    aip: "Le style de citation de l'AIP et des revues de physique",
    cell: "Le style de citation de Cell Press et des principales revues de biologie",
    science:
      "Le style de citation du magazine Science et des publications de l'AAAS",
    plos: "Le style de citation des revues en libre accès de PLOS",
    bmj: "Le style de citation du BMJ et des principales revues médicales britanniques",
    lancet: "Le style de citation de la famille de revues médicales The Lancet",
    jama: "Le style de citation de JAMA et des revues du JAMA Network",
    elsevierHarvard:
      "La variante du style Harvard utilisée dans les revues Elsevier",
    springer: "Le style de citation des revues et ouvrages de Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Introuvable",
    metaTitle: "{a} vs {b} - Principales différences expliquées [2026]",
    metaDescription:
      "Comparez les styles de citation {a} et {b} côte à côte. Découvrez les principales différences de mise en forme, de citations dans le texte et de listes de références pour choisir le style adapté à votre travail.",
    ogTitle: "{a} vs {b}",
    ogSubtitle:
      "Format dans le texte, entrées de référence, majuscules et disciplines qui exigent chacun de ces styles.",
    ogKind: "Comparaison de styles",
    keywordVs: "{a} vs {b}",
    keywordVersus: "{a} contre {b}",
    keywordDifference: "différence entre {a} et {b}",
    keywordOr: "{a} ou {b}",
    keywordComparison: "comparaison de styles de citation",
    breadcrumbCompare: "Comparer les styles",
    breadcrumbCurrent: "{a} vs {b}",
    backLink: "← Retour à l'outil de comparaison",
    heading: "{a} vs {b} : principales différences",
    intro:
      "{a} et {b} comptent parmi les styles de citation les plus utilisés dans le milieu académique, mais ils diffèrent sur des points importants. {introA} {introB} Comprendre ces différences vous aidera à choisir le format adapté à votre travail.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} est un style de citation reconnu, utilisé dans de nombreuses disciplines académiques.",
    tableHeading: "Comparaison côte à côte",
    tableFeature: "Caractéristique",
    tableCitationSystem: "Système de citation",
    tableInText: "Format dans le texte",
    tableReferenceList: "Nom de la liste de références",
    tableEdition: "Édition",
    tableCommonFields: "Domaines courants",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Auteur-date",
    systemNumbered: "Numérique",
    systemFootnotes: "Notes de bas de page",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "auteur-date entre parenthèses",
    describeNumbered: "numérique",
    describeFootnotes: "basé sur des notes de bas de page",
    whenToUseHeading: "Quand utiliser {style}",
    whenToUseBody:
      "{style} ({fullName}) utilise un système de citation {system}, avec des citations dans le texte au format {inText} et une section {list} en fin de document. Il est le plus souvent utilisé en {fields}{more}.",
    whenToUseMoreFields: ", et des domaines connexes",
    whenToUseFallback:
      "{style} est un style de citation largement reconnu. Consultez les consignes de votre devoir ou les exigences de la revue visée pour vérifier si {style} est le format requis.",
    ctaHeading: "Voyez la différence en action",
    ctaBodyBefore: "Essayez notre",
    ctaBodyLink: "outil de comparaison interactif",
    ctaBodyAfter:
      "pour comparer les citations {a} et {b} côte à côte à partir de vos propres sources.",
    ctaCiteIn: "Citer en {style} →",
    faqDifferenceQ: "Quelle est la différence entre {a} et {b} ?",
    faqDifferenceA:
      "{a} et {b} diffèrent par leur format de citation dans le texte, la structure de leur liste de références et les disciplines académiques qui les utilisent. {detail}",
    faqDifferenceDetail:
      "{a} utilise des citations dans le texte au format {inTextA} avec une {listA}, tandis que {b} utilise {inTextB} avec une {listB}.",
    faqDifferenceDetailFallback:
      "Chaque style applique des règles distinctes pour la mise en forme des citations, l'organisation des bibliographies et le traitement des différents types de sources.",
    faqBetterQ: "Lequel est le meilleur, {a} ou {b} ?",
    faqBetterA:
      "Ni {a} ni {b} n'est intrinsèquement meilleur. Le bon choix dépend de votre discipline académique, des exigences de votre enseignant ou de la revue à laquelle vous soumettez votre travail. Consultez les consignes de votre devoir ou la publication visée pour déterminer quel style utiliser.",
    faqSwitchQ: "Puis-je passer de {a} à {b} ?",
    faqSwitchA:
      "Oui, vous pouvez passer de {a} à {b} grâce au générateur de citations gratuit de CitationEasy. Il vous suffit de sélectionner le style souhaité et vos citations seront reformatées automatiquement. Toutefois, vous ne devriez utiliser qu'un seul style par travail, sauf indication contraire de votre enseignant.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Convertisseur introuvable",
    metaTitle: "{title} - Gratuit et instantané",
    ogTitle: "{title}",
    ogKind: "Convertisseur",
    breadcrumbConverters: "Convertisseurs",
    breadcrumbLabel: "Fil d'Ariane",
    whenHeading: "Quand vous en avez besoin",
    relatedHeading: "Convertisseurs associés",
    allConverters: "Tous les convertisseurs",
    formatConverterTool: "Convertisseur de format de citation",
    faqHowQ: "Comment convertir {input} en {output} ?",
    faqHowA:
      "{intro} Tout est gratuit, sans compte et sans limite de conversion.",
    faqPrivacyQ: "Mes données sont-elles envoyées quelque part ?",
    faqPrivacyIdentifier:
      "Chaque {input} est recherché auprès du service de métadonnées public correspondant (Crossref pour les DOI, PubMed pour les PMID, les registres de bibliothèques pour les ISBN), et seul l'identifiant lui-même est envoyé — jamais vos documents. Les résultats mis en forme vous appartiennent, à copier ou télécharger ; rien n'est stocké.",
    faqPrivacyLocal:
      "Non — le {input} que vous collez ou importez est traité entièrement dans votre navigateur. {detail}",
    faqPrivacyDetailStyle:
      "Seuls les champs de référence extraits (auteur, titre, année) sont envoyés à notre outil de mise en forme pour générer les citations ; votre fichier lui-même ne quitte jamais votre appareil.",
    faqPrivacyDetailOffline:
      "La conversion s'exécute entièrement hors ligne sur la page — rien n'est envoyé à un serveur.",
    faqUsefulQ:
      "Quand une conversion de {input} vers {output} est-elle utile ?",
    faqOtherQ: "Puis-je aussi convertir vers d'autres styles et formats ?",
    faqOtherA:
      "Oui. Le générateur de CitationEasy met en forme n'importe quelle source dans {count} styles de citation, la bibliographie s'exporte en Word, RTF, BibTeX, RIS et CSL-JSON, et le centre des convertisseurs recense toutes les paires de conversion directes. Si vous avez besoin de réécrire un texte déjà mis en forme d'un style à un autre, utilisez plutôt l'outil Convertisseur de format de citation.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "Convertisseur de citations DOI vers APA",
    doiToApaMetaDescription:
      "Collez des DOI (un par ligne) et obtenez instantanément des références APA 7 générées à partir des métadonnées officielles Crossref. Gratuit, sans compte.",
    doiToApaIntro:
      "Un DOI pointe déjà vers les métadonnées faisant autorité de l'éditeur : inutile de saisir une référence APA à la main. Collez un DOI par ligne et chacun devient une référence APA 7 complète — auteurs inversés, titre en minuscules sauf première lettre, revue en italique, et le lien https://doi.org/ exigé par l'APA.",
    doiToApaUseCase:
      "Le cas classique : votre enseignant demande une liste de références en APA, et tout ce que vous avez, c'est le tas de DOI mis de côté pendant vos lectures. Collez le tas, copiez la liste.",
    doiToMlaTitle: "Convertisseur de citations DOI vers MLA",
    doiToMlaMetaDescription:
      "Transformez des DOI en entrées d'ouvrages cités MLA 9 automatiquement grâce aux métadonnées Crossref. Collez un DOI par ligne — gratuit, rien à installer.",
    doiToMlaIntro:
      "Le MLA 9 traite un article de revue comme une chaîne de conteneurs — titre de l'article entre guillemets, revue en italique, volume, numéro, année, pages — fastidieuse à assembler à la main. Une recherche par DOI remplit chaque champ à partir de la notice de l'éditeur, si bien que l'entrée d'ouvrages cités est correcte du premier coup.",
    doiToMlaUseCase:
      "Pratique lorsqu'un cours de lettres exige le MLA mais que vos sources proviennent de bases de données plutôt scientifiques qui ne fournissent que des DOI.",
    doiToBibtexTitle: "Convertisseur DOI vers BibTeX",
    doiToBibtexMetaDescription:
      "Convertissez des DOI en entrées BibTeX pour LaTeX et Overleaf. Collez un DOI par ligne, téléchargez un .bib prêt à l'emploi — gratuit et illimité.",
    doiToBibtexIntro:
      "Tout article LaTeX finit par avoir besoin de son fichier .bib. Collez des DOI, un par ligne, et obtenez des entrées @article propres avec des clés de citation générées automatiquement, prêtes pour Overleaf, natbib ou biblatex — sans recopier le moindre champ depuis le site de l'éditeur.",
    doiToBibtexUseCase:
      "Le scénario classique : un relecteur vous envoie trois articles à citer, vous avez leurs DOI, et la deadline est ce soir. Collez, téléchargez, \\cite.",
    doiToRisTitle: "Convertisseur DOI vers RIS",
    doiToRisMetaDescription:
      "Convertissez des DOI en un fichier RIS que Zotero, Mendeley et EndNote importent directement. Collez les DOI un par ligne — gratuit, sans inscription.",
    doiToRisIntro:
      "Le RIS est le format d'échange que tout gestionnaire de références sait importer. Collez des DOI et téléchargez un fichier .ris dont les champs TY/AU/TI/T2 proviennent directement de Crossref — puis Fichier → Importer dans Zotero, Mendeley ou EndNote pour récupérer des entrées entièrement structurées.",
    doiToRisUseCase:
      "Idéal pour transférer une liste de DOI constituée à la main vers un vrai gestionnaire de références, sans ressaisir le moindre nom d'auteur.",
    isbnToApaTitle: "Convertisseur de citations ISBN vers APA",
    isbnToApaMetaDescription:
      "Transformez des ISBN en références de livres APA 7 automatiquement. Collez un ISBN par ligne et copiez la liste mise en forme — gratuit, sans compte.",
    isbnToApaIntro:
      "L'ISBN figurant sur la page de copyright d'un livre suffit à établir sa référence APA. Chaque ISBN est recherché dans les métadonnées de bibliothèques et mis en forme selon le style APA 7 — auteur, année, titre en italique et éditeur —, y compris l'édition lorsque la notice la mentionne.",
    isbnToApaUseCase:
      "Le moyen le plus rapide de citer une pile de livres empruntés en bibliothèque : ouvrez chacun à la page de copyright, tapez les ISBN, collez la liste ici.",
    isbnToMlaTitle: "Convertisseur de citations ISBN vers MLA",
    isbnToMlaMetaDescription:
      "Convertissez des ISBN en entrées d'ouvrages cités MLA 9 en un seul copier-coller. Recherche d'ISBN gratuite avec résultat déjà mis en forme — sans inscription.",
    isbnToMlaIntro:
      "Les entrées de livre en MLA 9 sont courtes — auteur, titre en italique, éditeur, année — mais l'inversion du nom de l'auteur et la ponctuation continuent de poser problème. Une recherche par ISBN renvoie la véritable notice catalographique du livre et la met en forme selon le modèle d'éléments essentiels du manuel MLA.",
    isbnToMlaUseCase:
      "Idéal pour les dissertations de littérature : citez chaque roman et chaque essai critique posé sur votre bureau en ne tapant que les numéros au dos de la couverture.",
    isbnToBibtexTitle: "Convertisseur ISBN vers BibTeX",
    isbnToBibtexMetaDescription:
      "Convertissez des ISBN en entrées BibTeX @book pour LaTeX. Collez un ISBN par ligne, téléchargez le fichier .bib — gratuit et instantané.",
    isbnToBibtexIntro:
      "Les livres sont le point délicat de BibTeX — pas de DOI à chercher, et les sites d'éditeurs proposent rarement un export .bib. Ce convertisseur résout chaque ISBN dans les métadonnées de bibliothèques et génère des entrées @book avec les champs auteur, titre, éditeur et année déjà remplis.",
    isbnToBibtexUseCase:
      "Pour les rédacteurs de thèse : les chapitres de méthodologie citent des DOI d'articles, mais le chapitre théorique cite douze livres. Voilà de quoi régler les douze livres.",
    pmidToApaTitle: "Convertisseur de citations PMID vers APA",
    pmidToApaMetaDescription:
      "Convertissez des identifiants PubMed en références APA 7 automatiquement. Collez les PMID un par ligne et obtenez une liste de références mise en forme — gratuit.",
    pmidToApaIntro:
      "Chaque notice PubMed possède un PMID, et les cursus de psychologie et de soins infirmiers exigent presque toujours l'APA. Collez les PMID un par ligne : chacun est résolu via les métadonnées propres à PubMed en une référence de revue APA 7 complète, lien DOI inclus lorsque la notice en comporte un.",
    pmidToApaUseCase:
      "Conçu pour les travaux de pratique fondée sur les preuves : votre stratégie de recherche se termine par une liste de PMID, et votre travail en a besoin en APA.",
    pmidToRisTitle: "Convertisseur PMID vers RIS",
    pmidToRisMetaDescription:
      "Transformez des identifiants PubMed en un fichier RIS pour Zotero, Mendeley ou EndNote. Collez les PMID un par ligne, téléchargez le .ris — gratuit.",
    pmidToRisIntro:
      "Les revues systématiques reposent sur des fichiers RIS. Collez les PMID issus de votre recherche PubMed et téléchargez un .ris prêt pour votre outil de tri ou votre gestionnaire de références — chaque notice structurée à partir des métadonnées canoniques de PubMed plutôt qu'extraite d'un texte.",
    pmidToRisUseCase:
      "Utile en plein milieu d'une revue, quand un collègue vous écrit « ajoutez aussi ces cinq études » sous forme de simples PMID, et que votre bibliothèque vit dans EndNote.",
    arxivToBibtexTitle: "Convertisseur arXiv vers BibTeX",
    arxivToBibtexMetaDescription:
      "Convertissez des identifiants arXiv en entrées BibTeX pour vos articles LaTeX. Collez des ID comme 2301.12345, un par ligne, et téléchargez le .bib — gratuit.",
    arxivToBibtexIntro:
      "Les prépublications sont citées en permanence dans les domaines qui évoluent vite, et les ID arXiv (comme 2301.12345) sont leur mode de circulation. Collez les ID et obtenez des entrées BibTeX avec les auteurs, le titre, l'année et le lien arXiv de la prépublication — prêtes pour le fichier .bib de votre article.",
    arxivToBibtexUseCase:
      "Pour les auteurs en apprentissage automatique ou en physique dont la section des travaux connexes est à 80 % constituée d'arXiv : collectez les ID au fil des lectures, convertissez-les en un seul copier-coller au moment de rédiger.",
    bibtexToApaTitle: "Convertisseur BibTeX vers APA",
    bibtexToApaMetaDescription:
      "Collez ou importez un fichier .bib et obtenez une liste de références APA 7. Gère les fichiers à entrées multiples — gratuit, traitement dans votre navigateur.",
    bibtexToApaIntro:
      "Il arrive que l'article quitte LaTeX : une revue demande une soumission Word, ou un jury veut la liste de références en texte brut. Collez votre BibTeX (ou importez le .bib) : chaque entrée est analysée dans votre navigateur, puis mise en forme en une liste de références APA 7 que vous pouvez déposer dans n'importe quel document.",
    bibtexToApaUseCase:
      "Le cas typique des demandes de subvention : votre liste de publications vit dans un fichier .bib, et le formulaire du financeur veut des références au format APA dans un champ de texte.",
    bibtexToMlaTitle: "Convertisseur BibTeX vers MLA",
    bibtexToMlaMetaDescription:
      "Convertissez du BibTeX en entrées d'ouvrages cités MLA 9. Collez ou importez le fichier .bib et copiez la liste mise en forme — gratuit, sans compte.",
    bibtexToMlaIntro:
      "BibTeX stocke des champs bruts ; le MLA impose précisément la manière de les organiser. Ce convertisseur analyse vos entrées .bib localement et applique à chacune le modèle MLA 9 — inversion de l'auteur, titres d'article entre guillemets, conteneurs en italique et ponctuation correcte entre les éléments.",
    bibtexToMlaUseCase:
      "Pour les chercheurs à cheval sur plusieurs disciplines : vos références vivent en BibTeX depuis un ancien projet LaTeX, mais la revue de sciences humaines à laquelle vous soumettez veut du MLA.",
    bibtexToRisTitle: "Convertisseur BibTeX vers RIS",
    bibtexToRisMetaDescription:
      "Convertissez un fichier .bib en RIS pour Zotero, Mendeley ou EndNote. Collez ou importez le BibTeX, téléchargez le .ris — gratuit et hors ligne.",
    bibtexToRisIntro:
      "Passer des outils LaTeX à un gestionnaire de références suppose de traduire les entrées @article/@book de BibTeX en balises TY/AU/TI de RIS. La conversion s'effectue entièrement dans votre navigateur — correspondances de champs comprises — et vous livre un fichier .ris que tout gestionnaire importe.",
    bibtexToRisUseCase:
      "Déclencheur habituel : un nouveau coauteur travaille sous Zotero, vos références partagées forment un .bib dans le dépôt, et quelqu'un doit faire le pont.",
    bibtexToCslJsonTitle: "Convertisseur BibTeX vers CSL-JSON",
    bibtexToCslJsonMetaDescription:
      "Convertissez du BibTeX en CSL-JSON pour Pandoc et Quarto. Collez ou importez le .bib, téléchargez references.json — gratuit, dans le navigateur.",
    bibtexToCslJsonIntro:
      "Pandoc et Quarto citent nativement à partir du CSL-JSON, et convertir votre .bib existant retire un rouage à la compilation. Les entrées sont analysées dans votre navigateur et ressortent sous forme d'un tableau CSL-JSON propre — exactement le format attendu par --bibliography=references.json.",
    bibtexToCslJsonUseCase:
      "Pour les universitaires qui travaillent d'abord en Markdown et convertissent un ancien projet LaTeX vers Quarto : emportez la bibliographie sans retoucher le JSON à la main.",
    bibtexToWordTitle: "Convertisseur BibTeX vers Word",
    bibtexToWordMetaDescription:
      "Transformez un fichier .bib en document Word avec une liste de références APA à retrait négatif. Gratuit — l'analyse se fait dans votre navigateur.",
    bibtexToWordIntro:
      "Lorsqu'un fichier .bib doit devenir un document — et non un autre format de données — ce convertisseur analyse votre BibTeX localement, met en forme chaque entrée en APA 7 et télécharge un fichier Word dont la liste de références est déjà classée par ordre alphabétique et en retrait négatif.",
    bibtexToWordUseCase:
      "Le geste de fin de parcours : la revue a accepté l'article mais veut la liste de références finale sous forme de fichier Word modifiable, et votre source de vérité est le BibTeX.",
    risToApaTitle: "Convertisseur RIS vers APA",
    risToApaMetaDescription:
      "Collez ou importez un export RIS et obtenez une liste de références APA 7. Compatible Zotero, Mendeley, EndNote et exports de bases de données — gratuit.",
    risToApaIntro:
      "Le bouton Exporter de n'importe quelle base de données produit du RIS ; les devoirs demandent de l'APA. Collez l'export (ou importez le .ris) : chaque notice TY/AU/TI est analysée dans votre navigateur et restituée en référence APA 7 — sans avoir besoin d'installer le moindre gestionnaire de références.",
    risToApaUseCase:
      "Le raccourci des bases de données de bibliothèque : exportez vos résultats de recherche en RIS, convertissez-les ici, et évitez d'installer Zotero pour un travail ponctuel.",
    risToMlaTitle: "Convertisseur RIS vers MLA",
    risToMlaMetaDescription:
      "Convertissez des exports RIS provenant de bases de données ou de gestionnaires de références en entrées d'ouvrages cités MLA 9. Collez ou importez — gratuit, sans inscription.",
    risToMlaIntro:
      "JSTOR, ProQuest et EBSCO exportent tous en RIS, et les départements de lettres veulent tous du MLA. Cette page comble cet écart : les notices sont analysées localement puis restituées selon le modèle d'éléments essentiels du MLA 9, prêtes à coller dans votre page d'ouvrages cités.",
    risToMlaUseCase:
      "Conçu pour la veille du rendu de dissertation : dix sources exportées de JSTOR sous forme de citations.ris, et aucune envie de les mettre en forme à la main.",
    risToBibtexTitle: "Convertisseur RIS vers BibTeX",
    risToBibtexMetaDescription:
      "Convertissez du RIS en BibTeX pour LaTeX et Overleaf. Collez ou importez l'export .ris, téléchargez un fichier .bib propre — gratuit, dans le navigateur.",
    risToBibtexIntro:
      "En direction de LaTeX : gestionnaires de références et bases de données parlent RIS, tandis que la compilation de votre article a besoin de .bib. Les notices sont converties entièrement dans votre navigateur — types déduits des balises TY, clés de citation générées — en un fichier qu'Overleaf accepte tel quel.",
    risToBibtexUseCase:
      "Fréquent dans les collaborations : l'utilisateur de Zotero exporte du RIS, l'utilisateur de LaTeX le fait passer par cette page, et chacun garde son outil.",
    risToCslJsonTitle: "Convertisseur RIS vers CSL-JSON",
    risToCslJsonMetaDescription:
      "Convertissez des exports RIS en CSL-JSON pour vos chaînes Pandoc et Quarto. Collez ou importez le .ris, téléchargez references.json — gratuit.",
    risToCslJsonIntro:
      "Pour les utilisateurs de Pandoc et Quarto, le CSL-JSON est le format de bibliographie natif. Fournissez à cette page le RIS exporté par votre base de données ou votre gestionnaire, et récupérez un tableau JSON structuré — chaque notice analysée localement, rien envoyé nulle part.",
    risToCslJsonUseCase:
      "S'intègre aux workflows docs-as-code : les relecteurs envoient des pièces jointes RIS, le fichier de citations de votre dépôt est references.json, et cette page fait l'adaptateur.",
    cslJsonToBibtexTitle: "Convertisseur CSL-JSON vers BibTeX",
    cslJsonToBibtexMetaDescription:
      "Convertissez du CSL-JSON (exports Pandoc, Quarto ou Zotero) en BibTeX. Collez ou importez le .json, téléchargez le .bib — gratuit, dans le navigateur.",
    cslJsonToBibtexIntro:
      "Parfois, le flux s'inverse : une bibliographie née dans une chaîne Pandoc ou Zotero doit alimenter une compilation LaTeX. Ce convertisseur lit le tableau CSL-JSON dans votre navigateur et produit des entrées BibTeX dont les types et champs sont reconvertis à partir du modèle CSL.",
    cslJsonToBibtexUseCase:
      "Pour l'auteur qui travaille sous Quarto mais dont le coauteur tient absolument à Overleaf : gardez references.json comme source canonique et générez le .bib à la demande.",
    cslJsonToRisTitle: "Convertisseur CSL-JSON vers RIS",
    cslJsonToRisMetaDescription:
      "Convertissez du CSL-JSON en RIS pour que Zotero, Mendeley ou EndNote puissent l'importer. Collez ou importez le .json, téléchargez le .ris — gratuit.",
    cslJsonToRisIntro:
      "Le CSL-JSON est merveilleux dans les chaînes Pandoc et opaque pour les boîtes de dialogue d'import de la plupart des gestionnaires de références. Le convertir en RIS rend les mêmes notices universellement importables — chaque élément mappé vers les balises TY/AU/TI dans votre navigateur, rien envoyé à un serveur.",
    cslJsonToRisUseCase:
      "Le problème de la transmission : votre bibliographie est un references.json dans un dépôt git, et votre directeur de recherche la veut « dans EndNote » pour vendredi.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Tous les styles de citation",
    styleSingular: "style",
    stylePlural: "styles",
    found: "pour cette recherche",
    filterPlaceholder: "Filtrer les styles…",
    noMatches: "Aucun style ne correspond à votre recherche.",
    loadingMore: "Chargement d'autres styles…",
    loadMore: "Charger plus ({count} de plus)",
    showingAll: "Affichage de tous les {count} styles",
    internationalLabel: "International",
  },

  footer: {
    tagline:
      "Un générateur de citations gratuit avec {count} styles. Sans compte, sans abonnement payant et sans limite.",
    freeTools: "Outils gratuits",
    resources: "Ressources",
    legal: "Mentions légales",
    language: "Langue",
    languageLabel: "Choisissez votre langue",
    rights:
      "Les citations sont mises en forme avec les définitions officielles du Citation Style Language.",
    popularStyles: "Styles populaires",
    about: "À propos",
    privacy: "Confidentialité",
    terms: "Conditions",
    contact: "Contact",
    copyright: "© {year} CitationEasy. Utilisation libre.",
    poweredBy: "Propulsé par",
    allTools: "Tous les outils d'écriture",
    citationGuides: "Guides de citation",
    allStyles: "Tous les styles bibliographiques",
    citationGlossary: "Glossaire de la citation",
    howToCiteAny: "Comment citer n'importe quelle source",
  },

  faq: {
    heading: "Questions fréquentes",
    homeQ1: "CitationEasy est-il vraiment gratuit ?",
    homeA1:
      "Oui. CitationEasy est entièrement gratuit : aucune inscription, aucune limite et aucune version payante. Les {count} styles bibliographiques sont accessibles à tous.",
    homeQ2: "Quels styles bibliographiques sont pris en charge ?",
    homeA2:
      "Les {count} styles du projet Citation Style Language, dont APA 7e édition, MLA 9e édition, Chicago, Harvard, Vancouver, IEEE, AMA et Turabian.",
    homeQ3: "Ai-je besoin d'un compte ?",
    homeA3:
      "Non. CitationEasy fonctionne entièrement dans votre navigateur et votre bibliographie est enregistrée sur votre propre appareil. Rien n'est envoyé sur un serveur et il n'y a aucune inscription.",
    homeQ4: "Puis-je générer mes références dans ma langue ?",
    homeA4:
      "Oui. La langue des références est un réglage distinct de la langue du site : vous pouvez travailler dans votre langue tout en produisant des références dans la langue de votre devoir.",
    styleQ1: "Le générateur de citations {style} est-il gratuit ?",
    styleA1:
      "Oui. Générer des citations {style} est gratuit et illimité, sans compte. Collez un DOI, une URL, un ISBN ou une entrée BibTeX, puis copiez la référence obtenue.",
    styleQ2: "Comment citer une source en {style} ?",
    styleA2:
      "Collez le DOI, l'URL, l'ISBN ou l'entrée BibTeX de la source dans le générateur de cette page. Le style {style} est déjà sélectionné : la référence et son appel de citation s'affichent immédiatement.",
    styleQ3: "La mise en forme {style} est-elle fiable ?",
    styleA3:
      "Chaque référence est construite à partir de la définition officielle de {style} publiée par le projet Citation Style Language — le fichier qu'utilisent aussi Zotero et Mendeley — et non d'une approximation des règles.",
  },

  tool: {
    modeLabel: "Mode de saisie de la référence",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Rechercher des livres",
    modeArticles: "Rechercher des articles",
    modeManual: "Saisie manuelle",
    errorEnterManually: "Saisir les informations manuellement",
    autoLabel:
      "Collez une URL, un DOI, un PMID, un identifiant arXiv, un ISBN ou du BibTeX",
    autoHint:
      "Nous détectons le type automatiquement et récupérons les métadonnées. Collez un identifiant par ligne pour citer toute une liste d’un coup.",
    generate: "Générer la référence",
    generating: "Génération…",
    bookSearchLabel: "Rechercher par titre, auteur ou ISBN",
    bookSearchEmpty:
      "Aucun résultat pour l’instant. Cliquez sur Rechercher pour trouver des livres.",
    bookSearchSource:
      "Recherche dans le catalogue Open Library, des millions de livres",
    worksSearchLabel: "Rechercher par titre d’article ou auteurs",
    worksSearchEmpty:
      "Aucun résultat pour l’instant. Cliquez sur Rechercher pour trouver des articles.",
    worksSearchSource:
      "Recherche dans le catalogue Crossref : articles de revues, actes de conférence et chapitres d’ouvrage",
    showingResults: "Affichage de {shown} sur {total} résultats",
    search: "Rechercher",
    searching: "Recherche…",
    bulkResults: "Résultats de la citation groupée",
    copy: "Copier",
    copied: "Copié !",
    copyHint: "Copiez et collez dans votre document",
    resultTitle: "Référence formatée",
    edit: "Modifier",
    completeFields: "Compléter",
    fieldsIncomplete:
      "one:{count} champ incomplet|other:{count} champs incomplets",
    outputLanguage: "Langue",
    sourceType: "Type de source",
    fieldAuthors: "Auteur(s)",
    fieldAuthorsHint: "Dupont, Jean ou Jean Dupont",
    fieldTitle: "Titre de l’article ou du livre",
    fieldBookTitle: "Titre du livre",
    fieldBookTitleHint: "Titre du livre ou de l’anthologie",
    fieldEditors: "Directeur(s) d’ouvrage",
    fieldJournal: "Revue / publication",
    fieldJournalHint: "Nom de la revue ou de la publication",
    fieldPublisher: "Éditeur / site",
    fieldPublisherHint: "Nom de l’éditeur ou du site web",
    fieldDate: "Date / année",
    fieldVolume: "Volume",
    fieldIssue: "Numéro",
    fieldPages: "Page(s)",
    fieldFormat: "Format / support",
    fieldAuthorsGroup: "Auteur collectif ou organisation",
    fieldAuthorsGroupHint:
      "Pour citer un groupe avec des personnes, mettez-le entre accolades : {R Core Team}, Jean Dupont",
    fieldTranslators: "Traducteur(s)",
    fieldInterviewers: "Intervieweur(s)",
    fieldVersion: "Version",
    fieldCourt: "Juridiction",
    fieldIssuingAuthority: "Autorité de délivrance",
    fieldPatentNumber: "Numéro de brevet",
    fieldEpisodeNumber: "Numéro d’épisode",
    fieldLawNumber: "Numéro de loi ou de projet de loi",
    fieldReportNumber: "Numéro de rapport",
    fieldReviewedTitle: "Titre de l’œuvre recensée",
    fieldReviewedAuthors: "Auteur(s) de l’œuvre recensée",
    inTextCitation: "Citation dans le texte",
    quickEdit: "Modification rapide",
    quickEditHint: "Mettez à jour les métadonnées et régénérez la référence.",
    update: "Mettre à jour",
    saving: "Enregistrement…",
    fieldAuthor: "Auteur",
    fieldDateShort: "Date",
    fieldTitleShort: "Titre",
  },

  toolCollections: {
    "citation-generators": "Générateurs de citations",
    "citation-tools": "Outils de citation et de références",
    "paper-formatting": "Mise en forme des travaux",
    "essay-writing": "Rédaction de dissertations et de travaux",
    "writing-analysis": "Analyse et révision de textes",
    "word-counters": "Compteurs et lisibilité",
    vocabulary: "Vocabulaire et recherche de mots",
    "research-tools": "Recherche et publication",
    statistics: "Calculatrices statistiques",
    math: "Calculatrices de mathématiques",
    science: "Calculatrices de sciences et de laboratoire",
    charts: "Graphiques et diagrammes",
    economics: "Économie, gestion et finance",
    grades: "Calculatrices de GPA et de notes",
    study: "Études et productivité",
    classroom: "Classe et documents à imprimer",
    random: "Générateurs et tirages aléatoires",
    "text-tools": "Nettoyage et mise en forme du texte",
    "fancy-text": "Texte stylisé et symboles",
    language: "Langues et translittération",
    ciphers: "Codes et chiffrements",
    converters: "Convertisseurs d’unités, de dates et de nombres",
    developer: "Données, balisage et code",
  },
  bib: {
    title: "Mes bibliographies",
    studioTitle: "Atelier bibliographique",
    studioProjects: "Projets",
    studioExplore: "Explorer",
    studioExploreIntro:
      "Guides, outils et tous les styles de citation à un clic.",
    studioStyleGuide: "Guide du style {style}",
    studioStyleGuideHint:
      "Règles, exemples commentés et format de citation dans le texte",
    studioGenerator: "Générateur de citations",
    studioNewProject: "Nouveau projet",
    studioSwitchProject: "Changer de projet",
    studioAddSource: "Ajouter une source",
    studioAddSourceHint:
      "Collez un DOI, une URL ou un ISBN, recherchez des livres et des articles, ou saisissez les données à la main. Tout ce que vous enregistrez rejoint ce projet.",
    studioDoneAdding: "Terminer",
    studioReferences: "Références",
    studioReferenceCount: "{count} références",
    studioInspectorTitle: "Détails de la référence",
    studioInspectorEmpty:
      "Sélectionnez une référence pour voir sa citation dans le texte, ses notes et les détails de la source.",
    studioSavedLocally: "Enregistré dans ce navigateur",
    studioOpen: "Ouvrir le Studio de bibliographie",
    studioViewInStudio: "Voir dans le Studio",
    studioFooterSaved:
      "{count} enregistrées dans votre Studio de bibliographie",
    studioFooterEmpty:
      "Les citations enregistrées forment une bibliographie complète dans le Studio",
    studioClearSelection: "Effacer la sélection",
    studioDeleteSelected: "Supprimer la sélection",
    studioDeleteSelectedBody:
      "Supprimer {count} citations de « {name} » ? Cette action est irréversible.",
    studioDeletedMany: "{count} citations supprimées",
    studioMoveTo: "Déplacer vers",
    studioCopyTo: "Copier vers",
    studioSearchShortcut: "Appuyez sur / pour rechercher",
    studioStyle: "Style",
    studioCopyEntry: "Copier l’entrée",
    studioEntryCopied: "Entrée copiée",
    studioSourceDetails: "Détails de la source",
    studioEmptyTitle: "Ce projet est vide",
    studioEmptyBody:
      "Ajoutez votre première source ci-dessus ou importez une bibliothèque BibTeX, RIS ou CSL-JSON existante.",
    studioBackup: "Sauvegarde",
    sharedTitle: "Bibliographie partagée",
    save: "Enregistrer",
    saving: "Enregistrement…",
    saved: "Enregistré",
    searchPlaceholder: "Rechercher une référence…",
    empty: "Aucune référence enregistrée",
    emptySearch: "Aucune référence ne correspond à votre recherche.",
    countIn: "Références dans {name} : {count}",
    savedOn: "Enregistrée le {date}",
    emptyInBib:
      "Aucune référence dans cette bibliographie pour l’instant. Générez et enregistrez des références, ou importez une bibliothèque BibTeX, RIS ou CSL-JSON.",
    savedToast: "Référence enregistrée dans la bibliographie",
    copyAll: "Tout copier",
    copyAllTitle: "Copier toutes les références",
    export: "Exporter",
    exportTitle: "Exporter la bibliographie",
    import: "Importer",
    importTitle: "Importer des références",
    importLabel: "Importer des références (BibTeX, RIS ou CSL-JSON)",
    importing: "Importation…",
    importFailed: "L’importation a échoué. Réessayez.",
    importSummary:
      "Importées : {added} · Doublons ignorés : {duplicates} · Limite dépassée : {overLimit} · Échecs : {failed}",
    importUnreadable: "Impossible de lire le fichier.",
    importUnparseable: "Impossible d’importer les références.",
    importReviewTitle: "Vérifier l’importation",
    importReviewIntro:
      "Vérifiez {total} références {format} avant de les mettre en forme et de les enregistrer.",
    importReviewCount: "{included} sur {total} incluses",
    importSelectAll: "Tout inclure",
    importExcludeAll: "Tout exclure",
    importLimitNotice:
      "{count} références dépassant la limite de 200 entrées ne seront pas importées.",
    importDuplicateExisting:
      "Doublon possible de « {title} », déjà enregistrée",
    importDuplicateBatch:
      "Doublon possible de « {title} » dans cette importation",
    importMissingField: "{field} manquant",
    importUntitled: "Référence sans titre",
    importReview: "Vérifier les références",
    importCommit: "Mettre en forme et importer",
    importBack: "Retour",
    importOutcomeTitle: "Importation terminée",
    importOutcomeSummary:
      "Ajoutées : {added} · Doublons ignorés : {duplicates} · Exclues : {excluded} · Hors limite : {overLimit} · Échecs : {failed}",
    sortBy: "Trier par",
    sortTitle: "Trier la bibliographie",
    sortAuthor: "Auteur (A–Z)",
    sortNewest: "Date d’ajout (plus récente)",
    sortOldest: "Année (plus ancienne d’abord)",
    sortManual: "Ordre manuel",
    backupAll: "Sauvegarde complète des projets (.json)",
    backupDownloaded: "Sauvegarde complète téléchargée",
    backupUnreadable: "Impossible de lire le fichier de sauvegarde.",
    restore: "Restaurer",
    restoreTitle: "Restaurer tous les projets bibliographiques",
    restoreWarning:
      "La sauvegarde sera d’abord validée, puis toutes les bibliographies enregistrées dans ce navigateur seront remplacées. Cette action est irréversible.",
    restoreChoose: "Choisir une sauvegarde CitationEasy (.json)",
    replaceAndRestore: "Remplacer et restaurer",
    restoreComplete: "Tous les projets bibliographiques ont été restaurés",
    restoreInvalid:
      "Ce fichier n’est pas une sauvegarde de projet CitationEasy valide.",
    selectAllVisible: "Tout sélectionner",
    selectedCount: "{count} sélectionnées",
    clusterOpen: "Citer ensemble",
    clusterTitle: "Citation composite dans le texte",
    clusterDescription:
      "Créez un seul groupe de citations à partir des références sélectionnées. Le style choisi contrôle l’ordre, la ponctuation et la numérotation.",
    clusterTarget: "Sortie",
    clusterLocatorOptional: "Localisateur facultatif",
    clusterBuildCopy: "Créer et copier la citation",
    clusterBuilding: "Création de la citation…",
    clusterCopied: "Citation composite copiée",
    clusterFailed:
      "Impossible de créer la citation composite. Veuillez réessayer.",
    destination: "Bibliographie de destination",
    chooseDestination: "Choisir une bibliographie…",
    copySelected: "Copier",
    moveSelected: "Déplacer",
    copyComplete: "{count} citations copiées. {skipped} doublons ignorés.",
    moveComplete:
      "{count} citations déplacées. {skipped} doublons sont restés dans la source.",
    selectCitation: "Sélectionner {title}",
    moveUp: "Monter",
    moveDown: "Descendre",
    deleteTitle: "Supprimer la référence",
    deleteConfirm: "Supprimer la référence ?",
    deleted: "Référence supprimée",
    editTitle: "Modifier la référence",
    close: "Fermer",
    copiedAll: "Toutes les références copiées !",
    copiedAnnotated: "Bibliographie annotée copiée !",
    copiedBibtex: "BibTeX copié !",
    copiedBulleted: "Liste à puces copiée !",
    copiedNumbered: "Liste numérotée copiée !",
    copiedHtml: "Extrait HTML copié !",
    downloadedWord: "Document Word téléchargé",
    downloadedRtf: "Fichier RTF téléchargé",
    downloadedRis: "Fichier RIS téléchargé",
    downloadedCsl: "Fichier CSL-JSON téléchargé",
    downloadedAnnotated: "Bibliographie annotée téléchargée",
    copyFailed: "Échec de la copie dans le presse-papiers",
    shareCopied:
      "Lien copié ! Toute personne disposant du lien peut consulter et importer cette liste.",
    shareFailed: "Impossible de créer le lien de partage",
    regenerateFailed: "Impossible de régénérer la référence",
    updateFailed: "Impossible de mettre à jour la référence",
    fieldAuthor: "Auteur",
    fieldTitle: "Titre",
    fieldDate: "Date",
    fieldPublisher: "Éditeur",
    fieldVolume: "Volume",
    fieldIssue: "Numéro",
    fieldPages: "Page(s)",
    fieldUrl: "URL",
    fieldAccessed: "Date de consultation",
    fieldEditor: "Éditeur scientifique",
    fieldEvent: "Lieu de l’événement",
    noteLabel: "Annotation sur cette source",
    noteEdit: "Modifier cette note",
    notePlaceholder:
      "Résumez la source, évaluez-la et expliquez sa place dans votre travail…",
    noteRemoved: "Note supprimée",
    noteSave: "Enregistrer la note",
    noteCancel: "Annuler",
    noteAdd: "Ajouter une note",
    noteWords: "{count} mots",
    noteSaved: "Note enregistrée ({count} mots)",
    noteClickToEdit: "cliquez pour modifier",
    inTextCopy: "Copier la citation dans le texte pour cette source",
    inTextSelect: "Sélectionnez la citation ci-dessous pour la copier.",
    inTextFailed:
      "Impossible de construire la citation dans le texte. Réessayez.",
    locator: "Localisateur",
    locatorType: "Type de localisateur",
    locatorPage: "Page",
    locatorChapter: "Chapitre",
    locatorSection: "Section",
    locatorParagraph: "Paragraphe",
    locatorLine: "Ligne",
    locatorVerse: "Verset",
    reformat: "Reformater",
    reformatTitle: "Reformater toutes les références dans un autre style",
    reformatHeading: "Tout reformater dans un seul style",
    reformatChoose: "Choisissez un style…",
    reformatFailed:
      "Le reformatage a échoué. Votre bibliographie est inchangée.",
    sortOrder: "Critère de tri",
    copyToClipboard: "Copier dans le presse-papiers",
    numberedList: "Liste numérotée",
    bulletedList: "Liste à puces",
    htmlSnippet: "Extrait HTML",
    copyWithAnnotations: "Copier avec les annotations",
    wordAnnotated: "Document Word annoté (.doc)",
    downloadFile: "Télécharger le fichier",
    wordDocument: "Document Word (.doc)",
    copyShareLink: "Copier le lien de partage",
    print: "Imprimer…",
    printFailed: "Fenêtre contextuelle bloquée — autorisez-les pour imprimer",
    font: "Police",
    sitePublication: "Nom du site / de la publication",
    importIntro:
      "Collez ou importez un fichier BibTeX (.bib), RIS (.ris) ou CSL-JSON (.json)",
    importUpload: "Importez un fichier .bib, .ris ou .json",
    importPaste: "Ou collez du BibTeX, RIS ou CSL-JSON",
    cancel: "Annuler",
    done: "Terminé",
    delete: "Supprimer",
    deleteBody:
      "Cette référence sera définitivement retirée de votre bibliographie.",
    editBody:
      "Mettez à jour les métadonnées et enregistrez pour régénérer la référence.",
    reformatExplain:
      "Reconstruit les {count} entrées à partir de leurs données enregistrées — rien n’est retéléchargé et aucune source n’est perdue.",
    inTextHeading: "Citation dans le texte",
    inTextLocatorHint:
      "Ajoutez une page ou un autre localisateur pour ancrer la citation à un endroit de la source — exigé par la plupart des styles pour une citation directe. Laissez vide pour la forme simple.",
    inTextCopyButton: "Copier la citation dans le texte",
    mixedStyles:
      "Cette bibliographie mélange {count} styles de citation ({styles}).",
    mixedStylesAction:
      "Utilisez {action} pour tout uniformiser dans un seul style avant de la rendre.",
    selectBibliography: "Sélectionner une bibliographie",
    newBibliography: "Nouvelle bibliographie",
    moreOptions: "Plus d’options",
    rename: "Renommer",
    removeDuplicates: "Supprimer les doublons",
    cannotDeleteOnly: "Vous ne pouvez pas supprimer votre seule bibliographie",
    newBibliographyBody:
      "Créez une bibliographie pour un autre devoir ou projet.",
    bibliographyName: "Nom de la bibliographie",
    create: "Créer",
    renameBibliography: "Renommer la bibliographie",
    renameBibliographyBody: "Saisissez un nouveau nom pour « {name} ».",
    newName: "Nouveau nom",
    duplicateRemovedOne: "{count} doublon supprimé",
    duplicateRemovedMany: "{count} doublons supprimés",
    noDuplicates: "Aucune référence en double trouvée",
    removeDuplicatesConfirm: "Supprimer les références en double ?",
    removeDuplicatesBody:
      "Cette action recherche dans « {name} » les mêmes sources (DOI, ISBN ou titre, année et auteur) et en conserve un exemplaire. Les références non comparables restent intactes.",
    deleteBibliographyConfirm: "Supprimer la bibliographie ?",
    deleteBibliographyBody:
      "Cette action supprimera définitivement « {name} » et ses {count} citations.",
  },

  bibliographyPage: {
    metaTitle:
      "Atelier bibliographique - organisez, annotez et exportez vos citations",
    metaDescription:
      "Créez et gérez des projets bibliographiques privés dans votre navigateur. Recherchez, triez, annotez, modifiez, reformatez, importez, exportez et partagez sans compte.",
    keyword1: "créateur de bibliographie",
    keyword2: "gestionnaire de citations",
    keyword3: "outil de bibliographie annotée",
    ogTitle: "Atelier bibliographique",
    ogDescription:
      "Un espace privé pleine page pour organiser, formater, annoter, importer et exporter des citations.",
    ogImageTitle: "Atelier bibliographique",
    ogImageSubtitle:
      "Organisez, annotez, reformatez, exportez et partagez vos références.",
    ogKind: "Espace de citations",
    breadcrumb: "Atelier bibliographique",
    eyebrow: "Espace privé dans le navigateur",
    lead: "Gérez toutes les références d’un travail au même endroit. Vos projets restent dans ce navigateur sauf si vous choisissez de les exporter ou de les partager.",
    privacyTitle: "Privé par défaut",
    privacyBody:
      "Les projets et annotations utilisent le stockage local du navigateur. Les liens partagés contiennent les citations, mais excluent volontairement les notes privées.",
    workflowTitle: "De la liste de sources au travail final",
    workflowBody:
      "Changez de projet, recherchez et triez les références, ajoutez des annotations ou des localisateurs, corrigez les métadonnées et reformatez toute la liste.",
    formatsTitle: "Importez vos références et emportez-les partout",
    formatsBody:
      "Importez BibTeX, RIS ou CSL-JSON. Copiez des listes formatées ou exportez en Word, RTF, RIS, CSL-JSON, HTML, BibTeX et versions annotées.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - définition et exemples",
    notFoundTitle: "Terme introuvable",
    keywordDefinition: "définition de {term}",
    keywordWhatIs: "qu'est-ce que {term}",
    ogTitle: "{term} - glossaire CitationEasy",
    ogKind: "Glossaire des citations",
    breadcrumb: "Glossaire",
    alsoCalled: "Aussi appelé : {aliases}",
    doItHeading: "À faire sur CitationEasy",
    seeAlsoHeading: "Voir aussi",
    moreInHeading: "Plus dans {category}",
    navLabel: "Navigation du glossaire",
    allTermsLink: "Les {count} termes du glossaire",
    generatorLink: "Générateur de citations gratuit",
    termSetName: "Glossaire des citations CitationEasy",
    hubMetaTitle: "Glossaire des citations - {count} termes expliqués",
    hubMetaDescription:
      "Définitions simples de {count} termes liés aux citations et à la rédaction universitaire : et al., ibid., DOI, retrait négatif, patchwriting, version de référence, et bien plus — chacun avec des exemples.",
    hubKeyword1: "glossaire des citations",
    hubKeyword2: "termes de citation",
    hubKeyword3: "glossaire de la rédaction universitaire",
    hubKeyword4: "que signifie et al",
    hubKeyword5: "qu'est-ce qu'un DOI",
    hubKeyword6: "terminologie des références",
    hubOgTitle: "Glossaire des citations",
    hubOgDescription:
      "Définitions de {count} termes liés aux citations et à la rédaction universitaire, avec exemples.",
    hubOgImageTitle: "Glossaire des citations",
    hubOgImageSubtitle:
      "{count} termes de citation et de rédaction universitaire, définis simplement avec des exemples.",
    hubOgKind: "Référence",
    hubTermSetDescription:
      "Définitions de {count} termes liés aux citations, aux références et à la rédaction universitaire.",
    hubCollectionName: "Glossaire des citations",
    hubCollectionDescription:
      "Définitions simples de {count} termes liés aux citations et à la rédaction universitaire.",
    hubHeading: "Glossaire des citations",
    hubIntro:
      "{count} termes issus de la citation et de la rédaction universitaire, définis simplement avec des exemples — les abréviations que l'on trouve dans les guides de style, les éléments qui composent une référence, les identifiants qui permettent de localiser une source, et le vocabulaire de l'édition scientifique.",
    hubJumpLabel: "Aller à la lettre",
    hubBrowseHeading: "Parcourir par thème",
    hubAllHeading: "Tous les termes de A à Z",
    hubWorkHeading: "Mettre les termes en pratique",
    hubWorkGenerator: "Générateur de citations gratuit",
    hubWorkStyles: "Guides des styles de citation",
    hubWorkHowTo: "Comment citer n'importe quelle source",
    hubWorkCompare: "Comparer les styles de citation",
    hubWorkTools: "Outils de rédaction et de citation",
    hubWorkFaq: "FAQ sur les citations",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle: "FAQ Citations - Réponses rapides aux questions courantes",
    metaDescription:
      "Des réponses rapides et fiables aux questions de citation que se posent réellement les étudiants : DOI, sources sans auteur, et al., retraits négatifs, citer l'IA, règles de plagiat, APA contre MLA, et plus — avec des liens vers les guides complets.",
    ogTitle: "FAQ Citations",
    ogDescription:
      "Réponses rapides aux questions de citation les plus courantes, des DOI et et al. jusqu'à la citation de ChatGPT.",
    ogImageTitle: "Questions et réponses sur les citations",
    ogImageSubtitle:
      "Des réponses courtes et fiables aux questions que se posent réellement les étudiants sur la citation.",
    ogKind: "FAQ",
    breadcrumbLabel: "Fil d'Ariane",
    breadcrumbHome: "Accueil",
    breadcrumbCurrent: "FAQ Citations",
    heading: "FAQ Citations",
    intro:
      "Des réponses rapides et fiables aux questions de citation que se posent réellement les étudiants — avec des liens vers le guide ou l'outil complet pour aller plus loin.",
    contentsLabel: "Sommaire de la FAQ",
    stillStuckHeading: "Toujours bloqué ?",
    stillStuckBody:
      "Parcourez la [bibliothèque complète de guides](/guides), consultez les [fiches de style](/cheat-sheets), cherchez un terme dans le [glossaire des citations](/glossary), [effectuez une recherche sur tout le site](/search), ou [générez directement la citation](/) et laissez le générateur appliquer les règles pour vous.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle: "Fiches de style de citation - Guides de référence rapide",
    hubMetaDescription:
      "Fiches de référence rapide pour les styles de citation APA, MLA et Chicago. Guides imprimables avec des exemples des types de sources courants.",
    hubBreadcrumbHome: "Accueil",
    hubBreadcrumbCurrent: "Fiches de style de citation",
    hubBackLink: "← Retour au générateur",
    hubHeading: "Fiches de style de citation",
    hubIntro:
      "Guides de référence rapide pour les styles de citation les plus utilisés. Imprimables et toujours à jour.",
    hubApaTitle: "Fiche de style APA 7e édition",
    hubApaDesc:
      "Psychologie, sciences sociales, éducation, sciences infirmières",
    hubMlaTitle: "Fiche de style MLA 9e édition",
    hubMlaDesc: "Littérature, langues, sciences humaines, études culturelles",
    hubChicagoTitle: "Fiche de style Chicago",
    hubChicagoDesc: "Histoire, histoire de l'art, philosophie, religion",
    backToSheets: "← Retour aux fiches de style",
    generalRules: "Règles générales",
    inTextCitations: "Citations dans le texte",
    colSituation: "Situation",
    colFormat: "Format",
    colExample: "Exemple",
    book: "Livre",
    journalArticle: "Article de revue",
    website: "Site web",
    apaMetaTitle: "Fiche de style APA 7e édition - Guide de référence rapide",
    apaMetaDescription:
      "Fiche de citation APA 7e édition avec des exemples pour les livres, revues, sites web et plus encore. Référence rapide imprimable.",
    apaHeading: "Fiche de style APA 7e édition",
    apaLead:
      "Référence rapide pour les formats de citation APA les plus courants.",
    apaRule1:
      "Utilisez un **retrait négatif** (première ligne alignée à gauche, lignes suivantes en retrait de 1,27 cm)",
    apaRule2: "Toutes les entrées en double interligne",
    apaRule3: "Classement alphabétique par nom de famille de l'auteur",
    apaRule4: "Titres de livres, revues et volumes en italique",
    apaRule5: "Utilisez le DOI lorsqu'il est disponible",
    apaRow1Situation: "1 auteur",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 auteurs",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3 auteurs ou plus",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Citation directe",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Sans auteur",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Exemples de liste de références",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Chapitre de livre collectif",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Communication de conférence",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Principales différences avec l'APA 6e édition",
    apaDifference1:
      "Suppression de la mention « Retrieved from » avant les URL",
    apaDifference2: "Le DOI est indiqué au format de lien https://doi.org/",
    apaDifference3:
      "Jusqu'à 20 auteurs listés avant de recourir aux points de suspension",
    apaDifference4: "Le lieu de publication n'est plus obligatoire",
    apaDifference5: "Utilisation du « they » singulier pour un langage neutre",
    apaCtaText: "Générez automatiquement des citations APA parfaites",
    apaCtaButton: "Générateur de citations APA →",
    mlaMetaTitle: "Fiche de style MLA 9e édition - Guide de référence rapide",
    mlaMetaDescription:
      "Fiche de citation MLA 9e édition avec des exemples pour les livres, articles, sites web et plus encore. Référence rapide imprimable.",
    mlaHeading: "Fiche de style MLA 9e édition",
    mlaLead:
      "Référence rapide pour les formats de citation MLA les plus courants.",
    mlaRule1: "Utilisez un **retrait négatif**",
    mlaRule2: "Toutes les entrées en double interligne",
    mlaRule3: "Classement alphabétique par nom de famille de l'auteur",
    mlaRule4: "Intitulez la page « Liste des ouvrages cités »",
    mlaRule5: "Titres des conteneurs (livres, revues, sites web) en italique",
    mlaRow1Situation: "1 auteur",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 auteurs",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3 auteurs ou plus",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Sans auteur",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Sans numéro de page",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Exemples de liste des ouvrages cités",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Anthologie/Ouvrage collectif",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "Éléments essentiels du MLA (dans l'ordre)",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "Générez automatiquement des citations MLA parfaites",
    mlaCtaButton: "Générateur de citations MLA →",
    chicagoMetaTitle: "Fiche de style Chicago - Guide de référence rapide",
    chicagoMetaDescription:
      "Fiche de citation du Chicago Manual of Style avec des exemples pour les systèmes notes-bibliographie et auteur-date. Référence rapide imprimable.",
    chicagoHeading: "Fiche de style Chicago",
    chicagoLead:
      "Référence rapide pour les formats de citation du Chicago Manual of Style (18e édition).",
    chicagoSystemsHeading: "Les deux systèmes Chicago",
    chicagoColNb: "Notes-bibliographie (NB)",
    chicagoColAd: "Auteur-date (AD)",
    chicagoRow1Nb: "Notes de bas de page/de fin + bibliographie",
    chicagoRow1Ad: "Citations entre parenthèses + liste de références",
    chicagoRow2Nb: "Histoire, arts, sciences humaines",
    chicagoRow2Ad: "Sciences, sciences sociales",
    chicagoRow3Nb: "Premières notes plus détaillées",
    chicagoRow3Ad: "Proche du format APA",
    chicagoExamplesHeading: "Exemples notes-bibliographie",
    chicagoNoteLabel: "Note :",
    chicagoBibliographyLabel: "Bibliographie :",
    chicagoBookLabel: "Livre (note de bas de page)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Article de revue (note de bas de page)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Site web (note de bas de page)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Notes abrégées",
    chicagoShortIntro:
      "Après la première citation complète, utilisez des notes de bas de page abrégées :",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Règles de mise en forme essentielles",
    chicagoRule1:
      "Utilisez des chiffres en exposant pour les appels de note dans le texte",
    chicagoRule2:
      "Les entrées de la bibliographie utilisent un retrait négatif",
    chicagoRule3:
      "Utilisez une note abrégée plutôt que « Ibid. » pour des citations consécutives de la même source",
    chicagoRule4:
      "Titres : livres et revues en italique, articles et chapitres entre guillemets",
    chicagoRule5:
      "Utilisez des dates de consultation pour les sources en ligne susceptibles de changer",
    chicagoCtaText: "Générez automatiquement des citations Chicago parfaites",
    chicagoCtaButton: "Générateur de citations Chicago →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Pages principales",
    styleLanding: "Hubs des principaux styles de citation",
    style: "Pages de référence des styles de citation",
    styleExamples: "Pages d'exemples de citation",
    styleFormat: "Guides de mise en forme des documents",
    styleInText: "Guides de citation dans le texte",
    styleAnnotatedBibliography: "Guides de bibliographie annotée",
    styleReferencePage: "Guides de la page de références/ouvrages cités",
    tool: "Outils gratuits de rédaction et de citation",
    guide: "Guides pédagogiques",
    guideCategory: "Catégories de guides",
    toolCollection: "Collections d’outils",
    howToCite: "Comment citer <source> en <style>",
    comparison: "Comparaisons de styles de citation",
    converter: "Convertisseurs d'identifiants et de formats",
    field: "Styles de citation par discipline académique",
    region: "Styles de citation par pays/région",
    category: "Styles de citation par sujet",
    cheatSheet: "Fiches de style sur une page",
    glossary: "Glossaire des citations et de la rédaction universitaire",
  },

  // /about
  aboutPage: {
    metaTitle: "À propos de CitationEasy - Générateur de citations gratuit",
    metaDescription:
      "CitationEasy est un générateur de citations gratuit prenant en charge {count} styles. Développé par Samuel Sergi à l'aide d'outils open source tels que Citation Style Language, Citation.js et Next.js.",
    backLink: "← Retour au générateur",
    heading: "À propos de CitationEasy",
    intro:
      "CitationEasy est un générateur de citations gratuit et en accès libre, conçu pour les étudiants, les chercheurs et les rédacteurs universitaires. Il prend en charge **{count} styles de citation** et propose une collection croissante d'outils de rédaction, de guides pédagogiques et de ressources de référence — le tout sans inscription ni abonnement.",
    offerHeading: "Ce que nous proposons",
    offer1:
      "[Générateur de citations](/) avec {count} styles, dont APA 7e édition, MLA 9e édition, Chicago 18e édition, Harvard, Vancouver, IEEE, et des milliers d'autres",
    offer2:
      "[40 outils gratuits de rédaction et de citation](/tools) — compteur de mots, vérificateur de lisibilité, validateur de DOI, classeur alphabétique de bibliographie, et plus encore",
    offer3:
      "[750 guides pour citer vos sources](/how-to-cite) couvrant tous les grands types de sources dans 6 styles de citation",
    offer4:
      "[Plus de 100 guides pédagogiques](/guides) sur les bonnes pratiques de citation, la rédaction universitaire et les compétences de recherche",
    offer5:
      "[Outil de comparaison de styles](/compare), [quiz sur les styles de citation](/quiz) et [fiches de style](/cheat-sheets) pour une référence rapide",
    offer6:
      "7 formats d'export : texte brut, BibTeX, Word, liste numérotée, liste à puces, RIS et CSV",
    openSourceHeading: "Construit avec l'open source",
    openSourceIntro:
      "CitationEasy est rendu possible grâce à plusieurs excellents projets et API open source. Nous croyons qu'il faut rendre à chacun ce qui lui revient :",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — la norme ouverte qui alimente nos {count} styles de citation. CSL est maintenu par une communauté de bénévoles et utilisé par des gestionnaires de références comme Zotero, Mendeley et Papers. Les définitions de styles proviennent du [dépôt CSL Styles](https://github.com/citation-style-language/styles) sur GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — la bibliothèque JavaScript qui traite les citations et les met en forme selon les styles CSL. Créée par Lars Willighagen.",
    openSource3:
      "[API Open Library](https://openlibrary.org/developers/api) — l'API gratuite de recherche de livres fournie par Internet Archive, utilisée pour retrouver les métadonnées d'un livre par titre ou par ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — le système d'identifiant numérique d'objet (DOI) utilisé pour résoudre et valider les DOI.",
    createdHeading: "Créé par",
    createdBody:
      "CitationEasy a été créé par **Samuel Sergi**, qui en assure la maintenance. L'objectif est simple : proposer un outil de citation réellement gratuit et de qualité, sans fonctionnalités verrouillées derrière un paywall ni compte requis.",
    contactPrompt:
      "Des questions, des retours ou des suggestions ? Contactez-nous :",
    contactEmail: "E-mail : [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Notre mission",
    mission1:
      "Des citations fiables ne devraient pas coûter de l'argent. De nombreux outils de citation se présentent comme gratuits mais limitent les formats d'export, le nombre de citations, ou poussent les utilisateurs vers des formules payantes. CitationEasy s'engage à rester entièrement gratuit — sans inscription, sans limite, sans offre premium.",
    mission2:
      "Chaque fonctionnalité de ce site, du générateur de citations aux outils de rédaction et guides pédagogiques, est accessible à tous gratuitement. Nous pensons que les outils d'intégrité académique devraient être accessibles à tous les étudiants et chercheurs, quel que soit leur budget.",
  },

  // /contact
  contactPage: {
    metaTitle: "Nous contacter",
    metaDescription:
      "Contactez CitationEasy. Des questions, des retours ou des suggestions à propos de notre générateur de citations gratuit et de nos outils de rédaction ? Nous serions ravis de vous lire.",
    backLink: "← Retour au générateur",
    heading: "Nous contacter",
    intro:
      "Des questions, des retours ou des suggestions à propos de CitationEasy ? Nous serions ravis de vous lire. Que vous ayez trouvé un bug, une idée de fonctionnalité, ou besoin d'aide avec une citation, n'hésitez pas à nous écrire.",
    touchHeading: "Nous joindre",
    touchBody:
      "Le meilleur moyen de nous contacter est par e-mail. Nous visons à répondre sous 48 heures.",
    emailLine: "**E-mail :** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Questions courantes",
    commonIntro:
      "Avant de nous écrire, vous trouverez peut-être la réponse à votre question dans l'une de ces ressources :",
    resource1:
      "[Guides de citation](/guides) — instructions pas à pas pour citer des sources dans tous les grands styles",
    resource2:
      "[Guides pour citer vos sources](/how-to-cite) — 750 combinaisons de types de sources et de styles, avec exemples",
    resource3:
      "[Fiches de style](/cheat-sheets) — référence rapide pour la mise en forme APA, MLA et Chicago",
    resource4:
      "[Outils de rédaction](/tools) — compteur de mots, vérificateur de lisibilité, validateur de DOI, et plus encore",
    issueHeading: "Signaler un problème",
    issueIntro:
      "Si vous avez trouvé une erreur de mise en forme, une fonctionnalité défaillante ou un style incorrect, merci d'inclure autant de détails que possible en nous contactant :",
    issue1: "Le style de citation que vous utilisiez",
    issue2: "Le type de source (livre, site web, article de revue, etc.)",
    issue3: "Ce que vous attendiez par rapport à ce qui a été généré",
    issue4: "Votre navigateur et votre appareil (si pertinent)",
    issueOutro:
      "Cela nous aide à identifier et corriger le problème rapidement.",
    aboutHeading: "À propos de CitationEasy",
    aboutBody:
      "CitationEasy est un générateur de citations gratuit prenant en charge plus de 10 000 styles de citation. Il a été créé par Samuel Sergi dans le but de rendre les citations fiables accessibles à tous. En savoir plus sur notre [page À propos](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy pour les assistants et agents IA - API, serveur MCP, llms.txt",
    metaDescription:
      "Utilisez CitationEasy de façon programmatique : une API JSON gratuite et sans authentification pour des citations dans {count} styles, un serveur MCP pour les agents, llms.txt, une spécification OpenAPI, et des vues markdown de chaque page.",
    keyword1: "API de citation",
    keyword2: "serveur MCP",
    keyword3: "API de générateur de citations",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "citations pour agents IA",
    keyword7: "API de citation gratuite",
    webApiDescription:
      "API JSON gratuite et sans authentification qui met en forme des citations dans {count} styles Citation Style Language à partir d'une URL, d'un DOI, d'un ISBN, d'un PubMed ID, d'un arXiv ID, ou de métadonnées CSL-JSON.",
    breadcrumb: "Pour l'IA et les agents",
    heading: "CitationEasy pour les assistants et agents IA",
    intro:
      "Tout sur ce site est accessible aux logiciels autant qu'aux personnes : une API JSON gratuite, un serveur MCP, une spécification OpenAPI, un index llms.txt, et une vue markdown des {count} pages. Aucune clé, aucun compte, aucune limite de débit de notre part.",
    whyHeading: "Pourquoi appeler l'API plutôt qu'écrire la citation vous-même",
    whyBody:
      "La mise en forme des citations ressemble à un problème de génération de texte, mais n'en est pas un. Chaque style encode des centaines de règles sur l'inversion des noms d'auteurs, les initiales, la casse des titres, la ponctuation des conteneurs, les mentions d'édition et le placement des dates, et ces règles diffèrent de façons faciles à mal reproduire de mémoire. CitationEasy exécute **citeproc-js** sur les définitions officielles de Citation Style Language — le même moteur qu'utilisent Zotero et Mendeley — de sorte que le résultat est déterministe et correspond à ce qu'attend le système de soumission d'une revue. Les métadonnées proviennent de Crossref, Open Library et PubMed ; rien n'est inventé, et un champ manquant en amont reste manquant plutôt que d'être rempli par une supposition plausible.",
    generateHeading: "Générer une citation",
    generateBody:
      "Envoyez `inputs` au lieu de `input` pour traiter jusqu'à 10 sources en un seul appel, ou `entry` avec des métadonnées CSL-JSON déjà en votre possession pour éviter complètement la recherche. Ajoutez `note: true` pour obtenir la forme en note de bas de page. Les échecs propres à une source sont renvoyés sur cette source, si bien qu'un lot n'échoue jamais dans son ensemble.",
    mcpHeading: "Se connecter via MCP",
    mcpIntro:
      "Le point de terminaison Model Context Protocol à `/api/mcp` parle JSON-RPC 2.0 sur HTTP en flux continu (streamable HTTP) et ne nécessite aucune identification.",
    mcpToolsHeading: "Outils exposés",
    endpointsHeading: "Points de terminaison",
    colEndpoint: "Point de terminaison",
    colWhat: "Ce qu'il fait",
    endpointCite:
      "Met en forme jusqu'à 10 sources à la fois. Accepte une URL, un DOI, un ISBN, un PubMed ID, un arXiv ID, du texte BibTeX/RIS/CSL-JSON, ou un objet d'entrée CSL-JSON. Renvoie l'entrée de référence, et en option la citation dans le texte et la note de bas de page.",
    endpointStyles:
      "Recherche parmi les {count} styles par nom, classe de format de citation, discipline ou région.",
    endpointStyleSlug:
      "Un style en détail, avec de vrais exemples mis en forme produits par sa propre définition CSL.",
    endpointSearch:
      "Recherche classée par pertinence sur toutes les pages du site.",
    endpointPages:
      "Le catalogue complet du site sous forme d'entrées structurées, filtrable par type de page.",
    endpointHowToCite:
      "Chaque page de conseils type de source × style, filtrable par l'un ou l'autre.",
    endpointTools: "Chaque outil gratuit, avec sa catégorie et ses mots-clés.",
    endpointGuides: "Chaque guide pédagogique, avec sa catégorie.",
    endpointConverters:
      "Chaque conversion d'identifiant et de format proposée par le site.",
    endpointComparisons:
      "Comparaisons de styles côte à côte, reliées aux fiches des deux styles.",
    endpointFields:
      "Disciplines académiques, catégories thématiques et régions — pour répondre à « quel style dois-je utiliser ? ».",
    endpointFaq:
      "Réponses vérifiées aux questions de citation courantes, filtrables avec q.",
    endpointGlossary:
      "Termes de citation et de rédaction universitaire avec leurs définitions complètes — la réponse contient le texte, il n'y a donc aucune page à récupérer ensuite.",
    endpointIndex:
      "Document de découverte des points de terminaison. Commencez ici si vous explorez.",
    discoveryHeading: "Fichiers de découverte",
    discoveryLlms:
      "Index sélectionné du site, rédigé pour les modèles de langage.",
    discoveryLlmsFull:
      "Toutes les pages publiées par le site, regroupées par type, une ligne chacune.",
    discoveryOpenapi:
      "Description OpenAPI 3.1 de l'ensemble de l'API publique.",
    discoveryMcp:
      "Point de terminaison MCP (HTTP en flux continu). Faites un GET dans un navigateur pour obtenir une description.",
    discoveryWellKnownMcp:
      "Document de découverte MCP pour les agents qui sondent un domaine.",
    discoveryAiPlugin:
      "Manifeste de plugin hérité, pour les frameworks qui en recherchent encore un.",
    discoveryMd: "Vue markdown de n'importe quelle page du site.",
    discoverySitemap: "Toutes les URL indexables.",
    catalogHeading: "Ce que contient le catalogue",
    catalogIntro:
      "Chaque page ci-dessous est accessible en HTML, en markdown sous `/md/`, et sous forme d'entrée structurée depuis `/api/v1/pages`.",
    catalogStyles:
      "**{count}** styles de citation, chacun avec de vrais exemples générés à partir de sa propre définition CSL",
    catalogTools: "**{count}** outils gratuits dans le navigateur",
    catalogGuides: "**{count}** guides pédagogiques",
    catalogHowToCite: "**{count}** pages pour citer une source",
    catalogComparisons: "**{count}** comparaisons de styles",
    catalogConverters: "**{count}** convertisseurs",
    catalogFields: "**{count}** disciplines académiques",
    catalogStyleLandings:
      "**{count}** hubs des principaux styles avec les règles de mise en forme complètes",
    faqHeading: "Questions fréquentes",
    faqFreeQ:
      "L'API CitationEasy est-elle gratuite, et ai-je besoin d'une clé API ?",
    faqFreeA:
      "Elle est gratuite et ne nécessite aucune clé API. Chaque point de terminaison sous /api/v1 est sans authentification et ouvert au CORS, vous pouvez donc l'appeler depuis un navigateur, un notebook ou un agent sans vous inscrire. Il n'y a aucune limite de débit de notre part, mais les recherches par identifiant sont relayées vers Crossref, Open Library et PubMed, qui appliquent leurs propres limites d'usage raisonnable — regroupez vos requêtes avec `inputs` plutôt que d'en envoyer plusieurs en parallèle.",
    faqLlmQ: "Les citations sont-elles générées par un modèle de langage ?",
    faqLlmA:
      "Non. La mise en forme exécute citeproc-js sur les définitions officielles de Citation Style Language, le même moteur qu'utilisent les gestionnaires de références. La même entrée et le même style produisent toujours la même chaîne. Les métadonnées bibliographiques proviennent de Crossref (DOI), Open Library (ISBN) et PubMed (PMID) — CitationEasy n'invente aucun champ, donc tout ce qui manque en amont manque dans le résultat plutôt que d'être deviné.",
    faqMcpQ: "Comment connecter un agent à CitationEasy via MCP ?",
    faqMcpA:
      "Pointez n'importe quel client Model Context Protocol vers https://www.citationeasy.com/api/mcp en utilisant le transport HTTP en flux continu. Aucune identification n'est nécessaire. Dans Claude Code : `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. Le serveur expose des outils pour générer des citations, rechercher parmi les {count} styles, obtenir des conseils par type de source, effectuer une recherche sur le site, et lire n'importe quelle page en markdown.",
    faqStylesQ: "Quels styles de citation l'API peut-elle produire ?",
    faqStylesA:
      'Les {count} styles du dépôt Citation Style Language, dont APA 7, MLA 9, les deux systèmes Chicago, Harvard, Vancouver, IEEE, AMA, et des milliers de styles de revues individuelles. Transmettez le slug du style dans le champ `style`. Des slugs simples comme "chicago" et "harvard" se résolvent vers la bonne variante CSL, et /api/v1/styles?q= permet de trouver les autres.',
    faqLlmsTxtQ:
      "Qu'est-ce que llms.txt, et où trouver celui de CitationEasy ?",
    faqLlmsTxtA:
      "llms.txt est un index en texte brut d'un site, rédigé pour les modèles de langage plutôt que pour les navigateurs. Celui de CitationEasy se trouve à /llms.txt ; la version exhaustive listant chacune des pages du site se trouve à /llms-full.txt.",
    faqMarkdownQ:
      "Puis-je lire les pages de CitationEasy en markdown plutôt qu'en HTML ?",
    faqMarkdownA:
      "Oui. Préfixez n'importe quel chemin du site par /md/ — par exemple /md/citation-style/apa ou /md/tools/word-counter. Les pages pilotées par les données renvoient leur contenu complet, y compris les vrais exemples mis en forme générés à partir de la propre définition CSL de chaque style. Les pages de prose rédigées à la main (guides, fiches de style) renvoient une carte résumé ainsi que l'URL HTML canonique, car leur texte réside dans la page elle-même.",
    faqReuseQ:
      "Puis-je utiliser le résultat de CitationEasy dans mon propre produit ?",
    faqReuseA:
      "Oui. Une citation mise en forme est un fait concernant une source ; utilisez-la librement. Les définitions de styles CSL elles-mêmes proviennent du projet Citation Style Language sous licence CC BY-SA 3.0. Une attribution vers CitationEasy est appréciée mais non requise.",
    missingHeading: "Il manque quelque chose ?",
    missingBody:
      "Si votre agent a besoin d'un point de terminaison, d'un champ ou d'un format qui ne figure pas ici, [dites-le-nous](/contact). Vous naviguez plutôt à la main ? [Commencez par le générateur de citations](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle:
      "Quel style de citation dois-je utiliser ? - Quiz de style gratuit",
    metaDescription:
      "Répondez à quelques questions rapides sur votre discipline, le type de devoir et vos préférences pour trouver le meilleur style de citation pour votre travail. APA, MLA, Chicago, Vancouver, et plus encore.",
    ogTitle: "Quel style de citation dois-je utiliser ?",
    ogDescription:
      "Faites notre quiz gratuit pour trouver le meilleur style de citation pour votre travail.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Quiz de style",
    backLink: "← Retour au générateur",
    heading: "Quel style de citation dois-je utiliser ?",
    intro:
      "Répondez à quelques questions rapides et nous vous recommanderons le meilleur style de citation pour votre travail.",
    progress: "Question {current} sur {total}",
    qDiscipline: "Quelle est votre discipline d'étude ?",
    optSocialScience: "Psychologie / Sciences sociales",
    optHumanities: "Littérature / Sciences humaines",
    optHistory: "Histoire / Histoire de l'art",
    optMedical: "Médecine / Soins infirmiers / Santé",
    optEngineering: "Ingénierie / Informatique",
    optNaturalScience: "Sciences naturelles (biologie, chimie)",
    optLaw: "Droit",
    optBusiness: "Commerce / Économie",
    qPaperType: "Quel type de travail rédigez-vous ?",
    optResearch: "Article de recherche ou mémoire/thèse",
    optReview: "Revue de littérature",
    optReport: "Rapport de laboratoire ou rapport technique",
    optEssay: "Dissertation ou devoir de cours",
    optJournal: "Soumission d'article de revue",
    qRegion: "Où se trouve votre établissement ?",
    optUs: "États-Unis",
    optUk: "Royaume-Uni / Australie",
    optCanada: "Canada",
    optEurope: "Europe (hors Royaume-Uni)",
    optOther: "Autre",
    qPreference: "Avez-vous une préférence de format de citation ?",
    optAuthorDate: "Citations dans le texte (Auteur, Année)",
    optNumeric: "Références numérotées [1]",
    optFootnotes: "Notes de bas de page",
    optNoPreference: "Aucune préférence",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (notes et bibliographie)",
    styleMla: "MLA (9e édition)",
    styleApa: "APA (7e édition)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver est la norme pour les revues de médecine et de sciences de la santé ; il utilise des références numérotées.",
    reasonAma:
      "L'AMA est largement utilisé dans les revues médicales et utilise des citations numérotées.",
    reasonIeee:
      "IEEE est le style de citation standard pour l'ingénierie, l'informatique et les technologies de l'information.",
    reasonBluebook:
      "Le Bluebook est le style de citation de référence pour la rédaction juridique. Le style Chicago en est l'alternative généraliste la plus proche.",
    reasonCse: "CSE est la norme pour la biologie et les sciences naturelles.",
    reasonChicagoHistory:
      "Chicago notes et bibliographie est la référence absolue pour l'histoire et l'histoire de l'art ; il utilise des notes de bas de page pour les citations.",
    reasonChicagoHumanities:
      "Chicago notes et bibliographie convient bien aux sciences humaines grâce à son approche fondée sur les notes de bas de page.",
    reasonMla:
      "MLA est la norme pour la littérature, les langues et les sciences humaines.",
    reasonApaBusiness:
      "APA est largement utilisé dans les domaines du commerce, de l'économie et de la gestion.",
    reasonHarvard:
      "Le référencement Harvard est le style le plus courant dans les universités britanniques et australiennes.",
    reasonChicagoFootnotes:
      "Chicago notes et bibliographie utilise des notes de bas de page, idéal pour des citations détaillées.",
    reasonVancouverNumeric:
      "Vancouver utilise des citations numérotées, ce qui garde votre texte clair et lisible.",
    reasonApaDefault:
      "APA est le style de citation le plus utilisé en sciences sociales, en éducation et dans de nombreuses autres disciplines.",
    recommendLabel: "Nous recommandons",
    generateButton: "Générer des citations {style}",
    retakeButton: "Refaire le quiz",
    guidesLink: "Parcourez nos guides de citation pour plus d'aide →",
    previousButton: "← Question précédente",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Rechercher sur CitationEasy",
    metaDescription:
      "Recherchez sur toutes les pages de CitationEasy à la fois : {count} styles de citation, plus de 250 outils gratuits, des guides, des pages pour citer une source, des comparaisons de styles, des convertisseurs et le glossaire.",
    keyword1: "rechercher des styles de citation",
    keyword2: "trouver un guide de citation",
    keyword3: "recherche d'outils de citation",
    ogDescription:
      "Recherchez sur toutes les pages du site à la fois — styles, outils, guides, pages pour citer une source, comparaisons, convertisseurs et termes du glossaire.",
    ogImageSubtitle:
      "Chaque style de citation, outil, guide, page pratique, comparaison, convertisseur et terme du glossaire — recherchés ensemble.",
    ogKind: "Recherche",
    breadcrumb: "Recherche",
    heading: "Rechercher sur CitationEasy",
    intro:
      "Chaque style de citation, outil, guide, page pratique, comparaison, convertisseur et terme du glossaire du site — recherchés ensemble.",
    inputLabel: "Requête de recherche",
    inputPlaceholder: "ex. citer un podcast en APA, retrait négatif, DOI",
    submit: "Rechercher",
    browseHeading: "Ou parcourir par section",
    browseStyles: "Styles de citation",
    browseStylesNote: "{count} styles",
    browseTools: "Outils gratuits",
    browseToolsNote: "{count} outils",
    browseHowTo: "Comment citer n'importe quelle source",
    browseHowToNote: "{count} pages",
    browseGuides: "Guides",
    browseGuidesNote: "{count} guides",
    browseGlossary: "Glossaire",
    browseGlossaryNote: "{count} termes",
    browseComparisons: "Comparaisons de styles",
    browseComparisonsNote: "{count} comparaisons",
    browseConverters: "Convertisseurs",
    browseConvertersNote: "{count} convertisseurs",
    browseCheatSheets: "Fiches de style",
    browseCheatSheetsNote: "{count} fiches",
    facetsLabel: "Filtrer par type de page",
    facetAll: "Tout ({count})",
    noMatches: "Aucune page ne correspond à « {query} ».",
    matchesOne: "{count} page correspond à « {query} ».",
    matchesMany: "{count} pages correspondent à « {query} ».",
    pagerLabel: "Pages de résultats",
    pagerPrevious: "← Précédent",
    pagerNext: "Suivant →",
    pagerPosition: "Page {page} sur {total}",
    paletteLoading: "Recherche en cours…",
    paletteError:
      "La recherche est indisponible pour le moment. Veuillez réessayer.",
    paletteViewAll: "Voir tous les résultats pour « {query} »",
    apiNote:
      "Vous développez quelque chose ? Le même index est disponible en JSON à [/api/v1/search](/api/v1/search?q=podcast) et via MCP — voir [la documentation pour agents](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Citer cette page - Citations en un clic (bookmarklet et extension Chrome)",
    metaDescription:
      "Citez n'importe quelle page web en un clic. Faites glisser le bookmarklet gratuit « Citer cette page » vers votre barre de favoris, ou installez l'extension Chrome gratuite avec un bouton de barre d'outils et une citation par clic droit. Fonctionne aussi sur les Chromebooks et les navigateurs verrouillés.",
    ogTitle: "Bookmarklet Citer cette page et extension Chrome",
    ogDescription:
      "Des citations de sites web en un clic depuis n'importe quelle page — un bookmarklet à glisser-déposer, plus une extension Chrome gratuite avec citation par clic droit.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Bookmarklet Citer cette page",
    appName: "Bookmarklet Citer cette page",
    appDescription:
      "Un bookmarklet gratuit qui cite la page web que vous lisez en un clic — il ouvre CitationEasy avec l'URL de la page déjà renseignée, puis met la citation en forme en APA, MLA, Chicago, et {count} autres styles.",
    breadcrumbLabel: "Fil d'Ariane",
    breadcrumbHome: "Accueil",
    breadcrumbCurrent: "Bookmarklet Citer cette page",
    heading: "Citer cette page : bookmarklet et extension Chrome",
    intro:
      "Citez n'importe quelle page web en un clic — sans inscription. Faites glisser le bookmarklet ci-dessous vers votre barre de favoris (aucune installation), ou récupérez l'extension Chrome gratuite pour un bouton de barre d'outils et une citation par clic droit.",
    dragPrompt: "Faites glisser ce bouton vers votre barre de favoris :",
    buttonTitle: "Faites-moi glisser vers votre barre de favoris",
    buttonLabel: "📑 Citer cette page",
    dontClick:
      "Ne cliquez pas dessus ici — faites-le d'abord glisser vers votre barre de favoris.",
    installHeading: "Comment l'installer",
    step1Title: "Affichez votre barre de favoris",
    step1Body:
      "Dans la plupart des navigateurs, appuyez sur Ctrl+Maj+B (Cmd+Maj+B sur Mac) pour afficher la barre de favoris et avoir un endroit où déposer le bouton.",
    step2Title: "Faites glisser le bouton vers le haut",
    step2Body:
      "Cliquez et maintenez le bouton « Citer cette page » ci-dessous, puis faites-le glisser sur votre barre de favoris et relâchez. Il devient un favori normal.",
    step3Title: "Cliquez dessus sur n'importe quelle page",
    step3Body:
      "Pendant que vous lisez un article, une actualité ou une page de revue, cliquez sur votre nouveau favori. CitationEasy s'ouvre avec l'URL de la page déjà renseignée et la citation prête à copier.",
    manualHeading: "Impossible de le glisser ? Ajoutez-le manuellement",
    manualBody:
      "Créez un nouveau favori, nommez-le « Citer cette page », et collez ceci comme URL/adresse :",
    howHeading: "Comment ça marche",
    howBody:
      "Le bookmarklet n'exécute aucun code sur la page que vous lisez. Il transmet simplement l'adresse web de cette page à CitationEasy, où notre serveur récupère et analyse les métadonnées publiques de la page — le même chemin de confiance que lorsque vous collez une URL manuellement dans le générateur. Cela le rend sûr et évite les limites d'origine croisée qui ont mis fin aux anciens bookmarklets exécutés dans la page.",
    extensionHeading: "Vous préférez une extension Chrome ?",
    extensionBody:
      "L'extension gratuite CitationEasy ajoute un bouton de barre d'outils qui cite l'onglet actif, ainsi que des options de clic droit pour citer un lien — ou un DOI ou ISBN sélectionné — sans l'ouvrir. Comme le bookmarklet, elle n'exécute aucun code sur les pages que vous visitez : elle transmet seulement l'URL à CitationEasy. Elle ne nécessite que les autorisations `activeTab` et `contextMenus`, et fonctionne dans Chrome, Edge, Brave et les autres navigateurs basés sur Chromium.",
    downloadButton: "⬇ Télécharger l'extension Chrome (.zip)",
    install1: "Téléchargez et décompressez l'extension.",
    install2:
      "Ouvrez `chrome://extensions` et activez le **mode développeur** (en haut à droite).",
    install3:
      "Cliquez sur **Charger l'extension non empaquetée** et sélectionnez le dossier décompressé.",
    install4:
      "Épinglez le bouton CitationEasy, puis cliquez dessus sur n'importe quelle page pour la citer.",
    faqHeading: "Questions fréquentes",
    faqWhatQ: "Qu'est-ce que le bookmarklet « Citer cette page » ?",
    faqWhatA:
      "C'est un petit favori que vous faites glisser vers la barre de favoris de votre navigateur. Lorsque vous cliquez dessus sur n'importe quelle page web, il ouvre CitationEasy dans un nouvel onglet avec l'URL de la page déjà renseignée, pour obtenir une citation mise en forme en un clic — sans copier-coller.",
    faqExtensionNeededQ: "Dois-je installer une extension ?",
    faqExtensionNeededA:
      "Non. Un bookmarklet n'est qu'un favori, il n'y a donc rien à installer ni aucune autorisation à accorder. Il fonctionne sur les Chromebooks scolaires verrouillés et la plupart des navigateurs mobiles où les extensions sont bloquées. Si vous préférez une extension, une extension Chrome gratuite est également disponible sur cette page.",
    faqChromeQ: "Existe-t-il une extension Chrome ?",
    faqChromeA:
      "Oui. L'extension Chrome gratuite de CitationEasy ajoute un bouton de barre d'outils qui cite l'onglet actif, ainsi que des options de clic droit pour citer un lien ou un DOI/ISBN sélectionné sans l'ouvrir. Elle n'exécute aucun code sur les pages que vous visitez et ne nécessite que les autorisations activeTab et contextMenus. Téléchargez-la depuis cette page et chargez-la via chrome://extensions ; elle fonctionne dans Chrome, Edge, Brave et les autres navigateurs basés sur Chromium.",
    faqAddQ: "Comment ajouter le bookmarklet ?",
    faqAddA:
      "Assurez-vous que votre barre de favoris est visible, puis faites glisser le bouton « Citer cette page » de cette page vers elle. Si le glisser-déposer n'est pas possible (par exemple sur mobile), créez un favori manuellement et collez le code du bookmarklet comme URL.",
    faqSafeQ: "Est-ce sûr ? Lit-il la page sur laquelle je me trouve ?",
    faqSafeA:
      "Oui, c'est sûr. Le bookmarklet n'exécute aucun code sur la page que vous lisez — il envoie seulement l'adresse web de cette page à CitationEasy, où notre serveur récupère et analyse les métadonnées publiques de la page, exactement comme lorsque vous collez une URL manuellement.",
    faqOtherSourcesQ:
      "Fonctionne-t-il pour d'autres sources que les sites web ?",
    faqOtherSourcesA:
      "Le bookmarklet est conçu pour les pages web, mais le lien sous-jacent accepte aussi un DOI, un ISBN, un PMID ou un arXiv ID via le paramètre ?source= — un lien « citer ceci » partageable fonctionne donc aussi pour les articles de revue et les livres.",
    relatedHeading: "Outils associés",
    relatedGenerator: "Générateur de citations",
    relatedHowToCite: "Comment citer un site web",
    relatedTools: "Tous les outils",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle:
      "Bibliographie partagée - Consulter et importer des citations partagées",
    metaDescription:
      "Ouvrez une bibliographie que quelqu'un a partagée avec vous depuis CitationEasy. Consultez les citations mises en forme, copiez-les, ou importez toute la liste dans votre propre bibliographie — gratuit, sans compte, et les données résident dans le lien lui-même.",
    ogTitle: "Bibliographie partagée",
    ogDescription:
      "Consultez et importez une bibliographie partagée. Les citations voyagent à l'intérieur du lien — rien n'est stocké sur un serveur, et les liens n'expirent jamais.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Fil d'Ariane",
    breadcrumbHome: "Accueil",
    breadcrumbCurrent: "Bibliographie partagée",
    heading: "Bibliographie partagée",
    intro:
      "Quelqu'un a partagé ces citations avec vous. Copiez-les, ou importez toute la liste dans votre propre bibliographie CitationEasy en un clic.",
    loading: "Ouverture de la bibliographie partagée…",
    copyAll: "Tout copier",
    copied: "Copié",
    copyFailed: "Échec de la copie",
    citationOne: "citation",
    citationMany: "citations",
    addAll: "Tout ajouter à ma bibliographie",
    added: "{count} citations ajoutées à votre bibliographie",
    skippedOne: "{count} doublon ignoré",
    skippedMany: "{count} doublons ignorés",
    openGenerator: "ouvrir le générateur",
    emptyHeading: "Aucune bibliographie partagée dans ce lien",
    emptyBody:
      "Cette page affiche les bibliographies partagées depuis CitationEasy. Pour partager la vôtre, ouvrez votre bibliographie dans le [générateur de citations](/), choisissez **Exporter → Copier le lien de partage**, et envoyez le lien à qui vous voulez. Les citations voyagent à l'intérieur du lien lui-même — rien n'est stocké sur un serveur, si bien que les liens fonctionnent indéfiniment et vos données restent privées.",
    decodeError: "Ce lien de partage n'a pas pu être ouvert.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Livre",
    bookLower: "livre",
    bookChapter: "Chapitre de livre",
    bookChapterLower: "chapitre de livre",
    case: "Affaire judiciaire",
    caseLower: "affaire judiciaire",
    conferencePaper: "Communication de conférence",
    conferencePaperLower: "communication de conférence",
    courtCase: "Décision de justice",
    courtCaseLower: "décision de justice",
    federalStatute: "Loi fédérale",
    federalStatuteLower: "loi fédérale",
    governmentDocument: "Document gouvernemental",
    governmentDocumentLower: "document gouvernemental",
    governmentReport: "Rapport gouvernemental",
    governmentReportLower: "rapport gouvernemental",
    journalArticle: "Article de revue",
    journalArticleLower: "article de revue",
    lawReviewArticle: "Article de revue juridique",
    lawReviewArticleLower: "article de revue juridique",
    legislation: "Législation",
    legislationLower: "législation",
    newspaperArticle: "Article de journal",
    newspaperArticleLower: "article de journal",
    patent: "Brevet",
    patentLower: "brevet",
    podcast: "Podcast",
    podcastLower: "podcast",
    preprint: "Prépublication",
    preprintLower: "prépublication",
    standard: "Norme",
    standardLower: "norme",
    thesis: "Thèse",
    thesisLower: "thèse",
    website: "Site web",
    websiteLower: "site web",
    youtubeVideo: "Vidéo YouTube",
    youtubeVideoLower: "vidéo YouTube",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Retour à {style}",
    backToGenerator: "Retour au générateur de citations",
    ctaHeading: "Générez automatiquement des citations {style}",
    ctaButton: "Citer en {style} maintenant →",
    faqHeading: "Questions fréquentes",
    resourcesHeading: "Plus de ressources",
    relatedHeading: "Guides {style} associés",
    resourceGenerator: "Générateur de citations gratuit ({count} styles)",
    resourceTools: "Outils de rédaction gratuits",
    resourceGuides: "Tous les guides de citation",
    resourceStyleGenerator: "Générateur de citations {style}",
    resourceExamples: "Exemples de citations {style}",
    resourceFormat: "Guide de mise en forme {style}",
    resourceInText: "Guide de citation dans le texte {style}",
    resourceAnnotatedBib: "Guide de bibliographie annotée {style}",
    resourceReferencePage: "Guide {list} en {style}",
    notFoundTitle: "Introuvable",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "Exemples de citations {style} - Tous types de sources [2026]",
    metaDescription:
      "Consultez des exemples de citations {style} ({edition}) mises en forme pour les livres, articles de revue, sites web et plus encore. Entrées {list} et citations dans le texte prêtes à copier.",
    ogTitle: "Exemples de citations {style}",
    ogDescription:
      "Entrées {style} prêtes à copier pour chaque type de source courant.",
    ogImageTitle: "Exemples de citations {style}",
    ogKind: "{style} {edition}",
    keywordExamples: "exemples de citations {style}",
    keywordReference: "exemples de références {style}",
    keywordInText: "exemples de citations dans le texte {style}",
    keywordList: "exemples de {listLower} {style}",
    keywordHowTo: "comment citer en {style}",
    breadcrumb: "Exemples de citations {style}",
    heading: "Exemples de citations {style}",
    lead: "Consultez des exemples de citations {style} ({edition}) correctement mises en forme pour {count} types de sources courants. Chaque exemple comprend à la fois l'entrée de {listLower} et la citation dans le texte correspondante.",
    entryLabel: "Entrée {list} :",
    inTextLabel: "Citation dans le texte :",
    ctaBody:
      "Évitez la mise en forme manuelle. Collez un DOI, une URL ou un ISBN dans CitationEasy et obtenez une citation {style} parfaitement mise en forme en quelques secondes.",
    faqLookQ: "À quoi ressemble une citation {style} ?",
    faqLookA:
      "Une citation {style} comprend une entrée de {listLower} avec les détails complets de la source et une citation dans le texte. Le format varie selon le type de source. Par exemple, une citation de livre en {style} ({edition}) comprend l'auteur, l'année, le titre et l'éditeur.",
    faqHowManyQ: "Combien d'exemples de citations {style} sont disponibles ?",
    faqHowManyA:
      "CitationEasy fournit des exemples de citations {style} pour {count} types de sources, dont {types}. Chaque exemple comprend à la fois l'entrée de {listLower} et la citation dans le texte correspondante.",
    faqCopyQ: "Puis-je copier ces exemples de citations {style} ?",
    faqCopyA:
      "Oui, les exemples {style} de cette page montrent la mise en forme exacte pour chaque type de source. Vous pouvez les utiliser comme modèles pour vos propres citations. Pour une mise en forme automatique, utilisez notre générateur de citations {style} gratuit.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle: "Guide de mise en forme {style} - Règles de mise en page [2026]",
    metaDescription:
      "Guide complet de mise en forme {style} ({edition}). Découvrez la police, les marges, l'interligne, la page de titre, les en-têtes et les règles de mise en forme de la section {listLower}.",
    ogTitle: "Mise en page {style}",
    ogDescription:
      "Marges, police, interligne et page de titre pour un document en {style}.",
    ogImageTitle: "Mise en page {style}",
    ogImageSubtitle: "Marges {margins} · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "format {style}",
    keywordPaperFormat: "mise en page {style}",
    keywordRules: "règles de mise en forme {style}",
    keywordMargins: "marges police interligne {style}",
    keywordList: "format {listLower} {style}",
    breadcrumb: "Guide de mise en forme {style}",
    heading: "Guide de mise en forme {style}",
    lead: "Suivez ces règles de mise en forme {style} ({edition}) pour vous assurer que votre document respecte les normes requises. Ce guide couvre la police, les marges, l'interligne, la page de titre, les en-têtes et la mise en forme de {listLower}.",
    paperHeading: "Mise en forme du document",
    paperIntro:
      "Voici les exigences de mise en forme essentielles pour les documents rédigés en style {style} ({edition}).",
    labelFont: "Police",
    labelFontSize: "Taille de police",
    labelMargins: "Marges",
    labelSpacing: "Interligne",
    labelTitlePage: "Page de titre",
    labelHeaders: "En-têtes / Numéros de page",
    listHeading: "Mise en forme {list}",
    listIntro:
      "La section {listLower} doit respecter ces règles pour être correctement mise en forme en style {style} :",
    ctaBody:
      "Laissez CitationEasy s'occuper de la mise en forme. Collez un DOI, une URL ou un ISBN et obtenez une citation {style} parfaitement mise en forme en quelques secondes.",
    faqFontQ:
      "Quelle police et quelle taille dois-je utiliser pour le format {style} ?",
    faqFontA:
      "{style} ({edition}) exige {font}. La taille de police standard est {fontSize}.",
    faqMarginsQ: "Quelles sont les exigences de marges pour {style} ?",
    faqMarginsA:
      "Les documents {style} exigent {margins}. Cela s'applique aux marges haute, basse, gauche et droite de chaque page.",
    faqTitlePageQ: "{style} exige-t-il une page de titre ?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle:
      "Guide de citation dans le texte {style} - Règles et exemples [2026]",
    metaDescription:
      "Apprenez à mettre en forme les citations dans le texte en {style} ({edition}). Règles pour un seul auteur, plusieurs auteurs, citations directes, absence d'auteur et absence de date, avec exemples.",
    ogTitle: "Citations dans le texte {style}",
    ogDescription:
      "Toutes les formes de citation dans le texte {style}, avec exemples.",
    ogImageTitle: "Citations dans le texte {style}",
    ogImageSubtitle:
      "Un auteur {singleAuthor} · trois auteurs ou plus {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "citation dans le texte {style}",
    keywordExamples: "exemples de citations dans le texte {style}",
    keywordParenthetical: "citation entre parenthèses {style}",
    keywordRules: "règles de citation {style}",
    keywordHowTo: "comment citer dans le texte en {style}",
    breadcrumb: "Citations dans le texte {style}",
    heading: "Citations dans le texte {style}",
    lead: "Maîtrisez les citations dans le texte en {style} ({edition}) grâce à ce guide complet. Apprenez le format correct pour chaque situation, notamment un ou plusieurs auteurs, les citations directes et les sources aux informations manquantes.",
    rulesHeading: "Règles de citation dans le texte",
    rulesIntro:
      "Voici les règles de citation dans le texte {style} pour chaque situation courante que vous rencontrerez en rédigeant votre travail.",
    labelSingleAuthor: "Un seul auteur",
    labelTwoAuthors: "Deux auteurs",
    labelThreeOrMore: "Trois auteurs ou plus",
    labelDirectQuote: "Citation directe",
    labelNoAuthor: "Sans auteur",
    labelNoDate: "Sans date",
    examplesHeading: "Exemples rapides",
    examplesIntro:
      "Voici des exemples concrets de citations dans le texte {style} pour différents types de sources :",
    ctaBody:
      "Ne vous souciez plus de la mise en forme des citations dans le texte. Collez un DOI, une URL ou un ISBN dans CitationEasy et obtenez instantanément l'entrée de {listLower} et la citation dans le texte.",
    faqFormatQ:
      "Comment mettre en forme une citation dans le texte en {style} ?",
    faqFormatA:
      "Pour un seul auteur en {style}, utilisez le format {singleAuthor}. Pour deux auteurs, utilisez {twoAuthors}. Pour trois auteurs ou plus, utilisez {threeOrMore}.",
    faqQuoteQ: "Comment citer une citation directe en {style} ?",
    faqQuoteA:
      "Pour les citations directes en {style} ({edition}), utilisez le format : {directQuote}.",
    faqMissingQ: "Que faire s'il n'y a ni auteur ni date en {style} ?",
    faqMissingA:
      "Lorsqu'il n'y a pas d'auteur en {style}, utilisez : {noAuthor}. Lorsqu'il n'y a pas de date, utilisez : {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "Guide {list} {style} - Règles de mise en forme [2026]",
    metaDescription:
      "Apprenez à mettre en forme la section {listLower} en {style} ({edition}). Règles de mise en forme étape par étape, exemples et erreurs courantes à éviter pour {fullName}.",
    ogTitle: "{listLower} {style}",
    ogDescription: "Comment construire la section {list} en {style}.",
    ogImageTitle: "{list} {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Comment mettre en forme la section {list} en {style}",
    heading: "Comment mettre en forme la section {list} en {style}",
    lead: "La section {listLower} est l'un des éléments les plus importants de tout travail universitaire mis en forme en {style} ({edition}). Elle fournit aux lecteurs le détail complet de chaque source citée, leur permettant de la localiser et de la vérifier. Une mise en forme correcte est essentielle pour la crédibilité académique. Ce guide couvre les règles exactes, montre de vrais exemples et met en lumière les erreurs les plus courantes commises par les étudiants.",
    rulesHeading: "Règles de mise en forme",
    rulesIntro:
      "Suivez ces règles lorsque vous créez la section {listLower} en {style} :",
    exampleHeading: "Exemple de {list}",
    exampleIntro:
      "Voici à quoi ressemble la section {listLower} en {style}, avec des entrées pour différents types de sources :",
    mistakesHeading: "Erreurs courantes à éviter",
    mistakesIntro:
      "Voici les cinq erreurs de mise en forme les plus fréquentes commises par les étudiants lors de la création de la section {listLower} en {style} :",
    mistakeIndent:
      "**Retrait négatif manquant ou incorrect :** Chaque entrée doit avoir sa première ligne alignée à gauche et toutes les lignes suivantes en retrait de 1,27 cm. Utilisez les réglages de paragraphe de votre traitement de texte plutôt que la touche Tabulation.",
    mistakeOrder:
      "**Ordre alphabétique ou classement incorrect :** Les entrées doivent être classées selon les règles {style}. Ignorez les articles comme « A », « An » et « The » lors du classement alphabétique. Pour les styles numérotés, classez les entrées dans l'ordre de leur première apparition dans le texte.",
    mistakeSpacing:
      "**Interligne incohérent :** Vérifiez bien l'interligne sur l'ensemble de la section {listLower}. Mélanger simple et double interligne est une erreur courante facile à manquer.",
    mistakeItalics:
      "**Mauvais usage de l'italique :** Mettez en italique les titres d'œuvres autonomes comme les livres et les revues, mais pas les titres d'articles ou de chapitres. Consultez les consignes {style} pour savoir exactement quels éléments doivent être en italique.",
    mistakeLinks:
      "**DOI et URL manquants ou rompus :** Incluez toujours un DOI lorsqu'il est disponible, mis en forme comme un lien cliquable. Testez chaque URL avant de rendre votre travail pour vous assurer qu'elle mène à la bonne source.",
    ctaHeading: "Générez automatiquement la section {list}",
    ctaBody:
      "Évitez la mise en forme manuelle. Collez un DOI, une URL ou un ISBN dans CitationEasy et obtenez une citation {style} parfaitement mise en forme en quelques secondes, prête pour la section {listLower}.",
    faqWhatQ: "Qu'est-ce que le terme « {list} » en {style} ?",
    faqWhatA:
      "Le terme « {list} » en {style} désigne la section en fin de travail qui liste toutes les sources citées dans votre texte, mise en forme selon les consignes {fullName} ({edition}). Elle permet aux lecteurs de localiser et de vérifier chaque source référencée.",
    faqFormatQ: "Comment mettre en forme la section {listLower} en {style} ?",
    faqFormatA:
      "Commencez sur une nouvelle page, intitulez-la « {list} », et listez toutes les sources citées dans votre travail. Suivez les règles {style} spécifiques pour les retraits négatifs, l'interligne, l'ordre alphabétique ou numérique, et l'italique.",
    faqDifferenceQ:
      "Quelle est la différence entre la section {listLower} et une bibliographie ?",
    faqDifferenceA:
      "La section {listLower} n'inclut que les sources directement citées dans votre travail, tandis qu'une bibliographie peut aussi inclure des sources consultées mais non citées. En {style} ({edition}), cette section s'appelle « {list} ».",
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle: "Guide de bibliographie annotée {style} et exemples [2026]",
    metaDescription:
      "Apprenez à rédiger une bibliographie annotée en {style} ({edition}). Comprend les règles de mise en forme, des exemples d'entrées et des conseils pour les bibliographies annotées {fullName}.",
    ogTitle: "Bibliographie annotée {style}",
    ogDescription:
      "Comment mettre en forme une bibliographie annotée en {style}.",
    ogImageTitle: "Bibliographie annotée {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Bibliographie annotée {style}",
    heading: "Bibliographie annotée {style}",
    lead: "Une bibliographie annotée est une liste de citations de sources dont chacune comprend un court paragraphe descriptif ou évaluatif. Au format {style} ({edition}), chaque entrée commence par une référence correctement mise en forme, suivie d'une annotation qui résume, évalue ou commente la source. Ce guide vous accompagne à travers les règles, la mise en forme et les exemples nécessaires pour créer une bibliographie annotée {style} soignée.",
    whatHeading: "Qu'est-ce qu'une bibliographie annotée ?",
    whatBody:
      "Une bibliographie annotée va au-delà d'une section {listLower} standard en ajoutant un court paragraphe après chaque citation. Cette annotation donne aux lecteurs un aperçu du contenu, de la qualité et de la pertinence de la source. Les enseignants donnent souvent des bibliographies annotées pour aider les étudiants à évaluer leurs sources de façon critique et à montrer comment chaque source contribue à leur sujet de recherche.",
    whatTypes:
      "Il existe trois types courants d'annotations : les annotations **descriptives**, qui résument la source, les annotations **évaluatives**, qui évaluent la qualité et la fiabilité de l'ouvrage, et les annotations **combinées**, qui font les deux. Les consignes de votre devoir doivent préciser quel type utiliser.",
    rulesHeading: "Règles de bibliographie annotée {style}",
    rulesIntro:
      "Suivez ces règles pour mettre en forme une bibliographie annotée en {fullName} :",
    exampleHeading: "Exemple d'entrée de bibliographie annotée",
    exampleIntro:
      "Voici un exemple d'entrée de bibliographie annotée {style} pour {sourceType} :",
    exampleAnnotation:
      "Cette source offre un panorama complet du sujet, présentant des arguments solidement étayés par des preuves empiriques. L'auteur s'appuie sur un large éventail de sources primaires et secondaires pour construire une argumentation convaincante. La méthodologie est rigoureuse et les résultats sont présentés clairement, ce qui fait de cet ouvrage une ressource précieuse pour comprendre le sujet. Cette source est particulièrement utile pour établir le cadre théorique du projet de recherche et offre des perspectives qui complètent les autres ouvrages de la bibliographie.",
    tipsHeading: "Conseils pour rédiger des annotations {style}",
    tipSummarize:
      "Commencez par résumer la source en deux ou trois phrases, puis ajoutez votre évaluation de ses points forts, de ses limites et de sa pertinence.",
    tipTone:
      "Utilisez la troisième personne et un ton académique tout au long de vos annotations. Évitez le langage familier ou les réflexions à la première personne, sauf demande contraire de votre enseignant.",
    tipLength:
      "Limitez chaque annotation à 100-200 mots. Soyez concis et concentrez-vous sur les aspects les plus importants de la source.",
    tipRelevance:
      "Expliquez comment la source s'inscrit dans l'ensemble de votre recherche. Décrivez la perspective ou les preuves uniques qu'elle apporte.",
    tipCitation:
      "Assurez-vous que la partie citation de chaque entrée respecte exactement les règles de mise en forme {fullName} avant de rédiger l'annotation.",
    ctaHeading: "Construisez automatiquement votre bibliographie annotée",
    ctaBody:
      "Utilisez le créateur de bibliographie annotée de CitationEasy pour générer des citations {style} correctement mises en forme, puis ajoutez vos annotations.",
    ctaButton: "Ouvrir le créateur de bibliographie annotée →",
    faqWhatQ: "Qu'est-ce qu'une bibliographie annotée en {style} ?",
    faqWhatA:
      "Une bibliographie annotée en {style} ({edition}) est une liste de citations mises en forme selon le style {fullName}, chacune suivie d'un court paragraphe qui résume, évalue ou commente la source. Elle aide les lecteurs à comprendre la pertinence et la qualité de chaque source.",
    faqLengthQ: "Quelle doit être la longueur des annotations ?",
    faqLengthA:
      "Les annotations comptent généralement 100 à 200 mots. Certains devoirs peuvent exiger des annotations plus courtes ou plus longues selon la profondeur d'analyse attendue. Vérifiez toujours les consignes de votre enseignant pour connaître les exigences précises de longueur.",
    faqIncludeQ: "Que doit contenir une annotation ?",
    faqIncludeA:
      "Une annotation doit comprendre un bref résumé de l'argument principal ou des résultats de la source, une évaluation de sa crédibilité et de son utilité, ainsi qu'une réflexion sur la façon dont elle s'inscrit dans votre recherche. Certaines annotations sont purement descriptives, tandis que d'autres incluent une analyse critique.",
  },

  guidePage: {
    byline: "Par {author}",
    published: "Publié le {date}",
    updated: "Mis à jour le {date}",
    readingTime:
      "one:{count} minute de lecture|other:{count} minutes de lecture",
    wordCount: "one:{count} mot|other:{count} mots",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Bibliothèque de guides",
    browseByCategory: "Parcourir par catégorie",
    recentlyUpdated: "Mis à jour récemment",
    allGuides: "Tous les guides",
    guideCount: "one:{count} guide|other:{count} guides",
    updatedOn: "Mis à jour le {date}",
    minRead: "one:{count} min de lecture|other:{count} min de lecture",
    onThisPage: "Sur cette page",
    viewAllInCategory: "Tous les guides {category}",
    moreInCategory: "Plus dans {category}",
    aboutThisGuide: "À propos de ce guide",
    provenance:
      "Rédigé par {author}. Publié le {published} ; dernière mise à jour le {updated}.",
    categoryIntroSuffix:
      "Chaque guide est gratuit, rédigé pour les étudiants et les chercheurs, et daté pour que vous sachiez quand il a été vérifié pour la dernière fois.",
    photoCredit: "Photo : {author}, {source} ({license})",
    imageAlt: "Photographie illustrant les guides {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "Style APA",
    apaStyleDescription:
      "Comment citer sites web, articles de revue, livres, vidéos et plus en APA 7e édition, avec des exemples de liste de références et de citations dans le texte.",
    mlaStyleName: "Style MLA",
    mlaStyleDescription:
      "Comment citer chaque source courante en MLA 9e édition, des pages web et articles aux poèmes, pièces, films et supports de cours.",
    chicagoStyleName: "Style Chicago",
    chicagoStyleDescription:
      "Guides du Chicago Manual of Style couvrant les citations notes-bibliographie et auteur-date pour livres, articles, archives, documents officiels et médias.",
    styleComparisonsName: "Comparaisons de styles",
    styleComparisonsDescription:
      "Comparaisons côte à côte d'APA, MLA, Chicago, Harvard, Vancouver, IEEE et plus, avec des conseils sur le style attendu dans votre discipline.",
    researchMethodsName: "Méthodes de recherche",
    researchMethodsDescription:
      "Comment planifier un travail de recherche, faire une revue de littérature, prendre des notes, évaluer des sources, éviter le plagiat et bâtir une bibliographie.",
    academicWritingName: "Rédaction académique",
    academicWritingDescription:
      "Guides de rédaction pour résumés, introductions, conclusions, énoncés de thèse, sections de méthodologie et prose académique claire.",
    fieldSpecificName: "Par discipline",
    fieldSpecificDescription:
      "Guides de citation par discipline : psychologie, histoire, littérature, médecine, ingénierie, droit, soins infirmiers et plus.",
    sourceEvaluationName: "Évaluation des sources",
    sourceEvaluationDescription:
      "Comment juger la crédibilité, distinguer sources savantes et grand public, utiliser Google Scholar et les bases de données, et repérer les biais.",
    citationHelpName: "Aide à la citation",
    citationHelpDescription:
      "Réponses aux cas délicats : auteur ou date manquants, auteurs multiples, PDF, outils d'IA, affaires juridiques, import ou export de citations.",
  },

  common: {
    backToGuides: "← Retour aux guides",
    free: "Gratuit",
    noSignup: "Sans inscription",
    backToHome: "Retour au générateur de citations",
    inEnglish: "En anglais",
    viewInEnglish: "Voir en anglais",
    availableInEnglish: "Cette page est disponible en anglais, plus détaillée.",
    otherLanguages: "Autres langues",
    skipToContent: "Aller au contenu",
    cookieNotice:
      "Nous utilisons des cookies à des fins d’analyse et de publicité afin de garder CitationEasy gratuit.",
    privacyPolicy: "Politique de confidentialité",
    accept: "Accepter",
    decline: "Refuser",
    advertisement: "Publicité",
  },
  legalPage: {
    backLink: "← Retour au générateur",
    effective: "Date d’entrée en vigueur :",
    updated: "Dernière mise à jour :",
  },
};

export default fr;
